import * as Y from '../../../../../shared/yjs';
// ObjectId imported elsewhere in this file; not needed here directly
import { nanoid } from 'nanoid';
import { marked } from 'marked';
import { parseMarkdownToTokens } from '../../../../../shared/config/markedConfig';
import { NodeId, NodeType, DocumentCommand, CommandContext, ReadResult, DocumentUtils, PatchLogger, Read } from './yDocHighLevelAPI';
import { traceLog } from '../../../../../services/debug/traceHelpers';
import { yDocLLMApiOrigin, yDocumentService } from './yDocLowLevelAPI';
// Deprecated resolver path removed in favor of direct store/service lookup
import { getDocsAppStore } from '../../../store/docsAppStore';
import useSpaceStore from '../../../../../stores/spaceStore';
import { docsCRDTService } from '../../crdt/docsCRDTService';
import { llmIdMapper } from '../../../../../utils/llmIdMapper';
import { YJSMarkdownBridge } from '../../../../../shared/utils/yjsMarkdownBridge';
import { StyleService } from '../../style/StyleService';
import { HtmlEntityDecoder } from '../../../../../shared/utils/htmlEntityDecoder';

// 🚨 CRITICAL FIX: Import MarkdownNormalizer to use same cleaning logic as INSERT operations
import { MarkdownNormalizer } from '../../../commands/v5/doc_write';

// Logger setup
import { createFileLogger } from '../../../../../utils/logger';
const log = createFileLogger('yDocLLMAPI', { enabled: true });

/**
 * Centralized helpers for document resolution and Markdown generation
 * (temporary home to simplify callsites without introducing a facade yet)
 */
export async function getDocContext(
  llmId: string,
  context: { spaceId?: string; traceId?: string; activeWindowId?: string; [k: string]: any }
): Promise<{
  yDoc?: Y.Doc;
  docContext?: CommandContext;
  docId?: string;
  windowId?: string;
  error?: string;
}> {
  try {
    const spaceId = context?.spaceId ? String(context.spaceId) : undefined;
    const activeWindowId = (context as any)?.activeWindowId || (context as any)?.windowId;

    // 1) Prefer the DocsApp store for the active window/space
    if (spaceId && activeWindowId) {
      try {
        const store = getDocsAppStore(activeWindowId, spaceId);
        const state = store.getState();
        if (state?.ydoc) {
          const yDoc = state.ydoc;
          const docId = state.docId || llmIdMapper.getActualId(llmId) || llmId;
          
          // 🔧 CRITICAL FIX: Verify this window actually contains the requested document!
          // Race condition: User creates new doc → activeWindowId not updated yet → Returns wrong doc
          // Check BOTH MongoDB ID and llmId to ensure correct document
          const actualIdForLlmId = llmIdMapper.getActualId(llmId);
          const llmIdForDocId = llmIdMapper.getLLMId(docId);
          
          const isCorrectDocument = (
            // Case 1: Requested by MongoDB ID and it matches
            (llmId === docId) ||
            // Case 2: Requested by llmId and mapper confirms it matches this docId
            (actualIdForLlmId === docId) ||
            // Case 3: Window's llmId matches requested llmId
            (llmIdForDocId === llmId)
          );
          
          if (!isCorrectDocument) {
            log.warn('[getDocContext] 🔧 RACE CONDITION: Active window has different document!', {
              requestedLlmId: llmId,
              requestedActualId: actualIdForLlmId,
              activeWindowDocId: docId,
              activeWindowLlmId: llmIdForDocId,
              activeWindowId,
              note: 'Falling through to find correct document'
            });
            // Fall through to other resolution methods
          } else {
            log.log('[getDocContext] ✅ Active window has correct document:', {
              requestedLlmId: llmId,
              docId,
              activeWindowId
            });
            // Document matches - safe to use
            const docContext: CommandContext = {
              yxml: yDoc.getXmlFragment('prosemirror'),
              doc: yDoc,
              docId,
              metadata: { windowId: activeWindowId, spaceId }
            } as any;
            return { yDoc, docContext, docId, windowId: activeWindowId };
          }
        }
      } catch (_) {
        // ignore and fall through
      }
    }

    // 2) Try space store windows (in case a non-active window holds the doc)
    try {
      const { windows } = useSpaceStore.getState();
      const target = Object.entries(windows).find(([_, w]: any) => {
        try { return (w as any)?.entityLlmId === llmId; } catch { return false; }
      });
      if (target && spaceId) {
        const [windowId] = target;
        const store = getDocsAppStore(windowId, spaceId);
        const state = store.getState();
        if (state?.ydoc) {
          const yDoc = state.ydoc;
          const docId = state.docId || llmIdMapper.getActualId(llmId) || llmId;
          const docContext: CommandContext = {
            yxml: yDoc.getXmlFragment('prosemirror'),
            doc: yDoc,
            docId,
            metadata: { windowId, spaceId }
          } as any;
          return { yDoc, docContext, docId, windowId };
        }
      }
    } catch (_) {
      // ignore and fall through
    }

    // 3) Fallback to CRDT service by doc id
    try {
      const actualId = llmIdMapper.getActualId(llmId) || llmId;
      
      // 🚨 CRITICAL: Check for PDF/Word simple nodes FIRST
      // This makes PDFs and Word docs behave exactly like regular documents
      let initialContent: any = undefined;
      let pageMargins: any = undefined;
      
      try {
        const { entityService } = await import('../../../../../services/entityService');
        const entity = await entityService.fetchEntity(actualId);
        
        // PDFs and Word docs are now handled by the DocumentReader abstraction
        // No Y.js conversion needed here - see DocumentReader.ts
        
        // 🔥 GOOGLE_DOCS_FIX: Fetch entity to get ProseMirror JSON for initialization
        // This is critical for imported Google Docs that haven't been opened yet
        if (entity && entity.skeleton?.content) {
          initialContent = entity.skeleton.content;
          // log.log(`🔥 [GOOGLE_DOCS_FIX] Found ProseMirror JSON for ${actualId}:`, {
          //   contentType: typeof initialContent,
          //   isProseMirrorJSON: typeof initialContent === 'object' && initialContent?.type === 'doc',
          //   nodeCount: initialContent?.content?.length || 0,
          //   willInitializeYDoc: true
          // });
        } else {
          // log.log(`🔥 [GOOGLE_DOCS_FIX] No content found in entity ${actualId}:`, {
          //   hasEntity: !!entity,
          //   hasSkeleton: !!entity?.skeleton,
          //   hasContent: !!entity?.skeleton?.content,
          //   skeletonKeys: entity?.skeleton ? Object.keys(entity.skeleton) : []
          // });
        }
        
        // Extract page margins from Word import metadata
        pageMargins = entity?.skeleton?.pageSetup?.margins;
      } catch (entityError) {
        console.warn(`🔥 [GOOGLE_DOCS_FIX] Could not fetch entity for ${actualId}:`, entityError);
      }
      
      const { doc } = await docsCRDTService.getYDocForDoc(actualId, initialContent, undefined, undefined, undefined, pageMargins);
      if (doc) {
        const yDoc = doc;
        const docContext: CommandContext = {
          yxml: yDoc.getXmlFragment('prosemirror'),
          doc: yDoc,
          docId: actualId,
          metadata: { windowId: activeWindowId, spaceId }
        } as any;
        
        // log.log(`🔥 [GOOGLE_DOCS_FIX] Y.js document initialized:`, {
        //   docId: actualId.substring(actualId.length - 8),
        //   yxmlLength: yDoc.getXmlFragment('prosemirror').length,
        //   hadProseMirrorJSON: !!initialContent,
        //   initializedWithContent: !!initialContent,
        //   success: true
        // });
        
        return { yDoc, docContext, docId: actualId, windowId: activeWindowId };
      }
    } catch (_) {
      // ignore
    }

    return { error: 'Y.js document not accessible' };
  } catch (e) {
    return { error: e instanceof Error ? e.message : String(e) };
  }
}

export function readAllToMarkdown(yDoc: Y.Doc): string {
  return YJSMarkdownBridge.toMarkdown(yDoc);
}

export function readRangeToMarkdown(docContext: CommandContext, fromId: NodeId, toId: NodeId): string {
  const range = Read.range(docContext, fromId, toId);
  return nodesArrayToMarkdown(range.nodes);
}

export function readNodesToMarkdown(docContext: CommandContext, nodeIds: NodeId[]): string {
  // Preserve order from document by scanning all nodes
  const all = Read.all(docContext).nodes;
  const requested = all.filter(n => nodeIds.includes(n.id));
  return nodesArrayToMarkdown(requested);
}

export function searchToMarkdown(docContext: CommandContext, query: string, limit: number = 50): string {
  const results = Read.search(docContext, query, { limit });
  return nodesArrayToMarkdown(results.nodes);
}

function nodesArrayToMarkdown(nodes: ReadResult['nodes']): string {
  const temp = new Y.Doc();
  const frag = temp.getXmlFragment('prosemirror');
  const elements = nodes.map(node => {
    const el = new (Y as any).XmlElement(node.type);
    if (node.id) el.setAttribute('id', node.id);
    if (node.attrs && typeof node.attrs === 'object') {
      // Basic support for heading level, etc.
      Object.entries(node.attrs).forEach(([k, v]) => {
        if (v !== undefined && v !== null) {
          try { el.setAttribute(k, v as any); } catch {}
        }
      });
    }
    // 🚨 CRITICAL FIX: DO NOT use toString() on node.text - causes fontFamily contamination!
    // This is used in AI conversion logic and contamination here affects ALL AI operations
    let text = '';
    try {
      if (node.text) {
        const anyText = node.text as any;
        if (typeof anyText === 'string') {
          text = anyText;
        } else if (anyText._content && typeof anyText._content === 'string') {
          text = anyText._content;
        } else if (typeof anyText.toString === 'function') {
          console.warn('[yDocLLMAPI] 🚨 Using dangerous toString() in AI conversion logic!');
          const contaminated = anyText.toString();
          
          // 🔍 QUOTE TRACE: Check if contaminated text has quotes before cleaning
          const hasQuotesInContaminated = /["']/.test(contaminated);
          if (hasQuotesInContaminated) {
            // log.log('🔍 [TEXT_CLEAN_TRACE] Contaminated text contains quotes:', {
            //   contaminatedPreview: contaminated.substring(0, 100),
            //   quotesFound: contaminated.match(/["']/g) || []
            // });
          }
          
          text = contaminated
            .replace(/<fontFamily[^>]*>.*?<\/fontFamily>/g, '')
            .replace(/<[^>]*>/g, '')
            .replace(/fontFamily/g, '');
        }
      }
    } catch (error) {
      console.error('[yDocLLMAPI] CRITICAL: Cannot extract text for AI conversion:', error);
      text = ''; // Fail safe for AI conversion
    }
    if (text.length > 0) {
      const t = new Y.XmlText();
      t.insert(0, text);
      el.insert(0, [t]);
    }
    return el;
  });
  if (elements.length > 0) frag.insert(0, elements as any);
  return YJSMarkdownBridge.toMarkdown(temp);
}

/*
  TODO [ARCH SIMPLIFY - TEMP NOTE]
  ----------------------------------------------------------------------
  Single doc resolver
  Change: Replace entityResolver.resolveDoc and getYDocForEntity with yDocFacade.getDocContext(llmId).
  Files: gui/src/apps/DocsApp/commands5/doc_replace.ts, .../commands5/doc_read.ts (and any others using both).
  Effect: one code path into Y.Doc; fewer imports, less branching.

  Kill temp Y.Doc builds in reads
  Change: Add yDocFacade.readRangeToMarkdown(...) and readNodesToMarkdown(...); replace manual new Y.XmlElement(...) assembly in doc_read_v2.
  Files: yDocFacade.ts [new], commands5/doc_read.ts.
  Effect: removes brittle Y.XmlElement construction; centralizes Markdown paths.
  ----------------------------------------------------------------------
*/

export class ReplaceWithMarkdown implements DocumentCommand {
  private revisionId: string;
  private batchDescription: string;
  private suggestionMode: boolean;
  private insertedElements: Y.XmlElement[] = [];
  private restrictIds?: string[];
  private defaultFontFamily?: string;

  constructor(
    private target: { from: NodeId; to: NodeId },
    private markdown: string,
    options: {
      revisionId?: string;
      batchDescription?: string;
      suggestionMode?: boolean;
      restrictIds?: string[]; // strictly limit deletions to these ids within the target span
    } = {}
  ) {
    this.revisionId = options.revisionId || `rev_${nanoid(10)}`;
    this.batchDescription = options.batchDescription || 'Replace with Markdown';
    this.suggestionMode = options.suggestionMode !== false;
    this.restrictIds = options.restrictIds;
    
    // 🚨 CRITICAL DEBUG: Track suggestionMode parameter flow
    // log.log('🚨 [SUGGESTION_MODE_DEBUG] === REPLACE WITH MARKDOWN CONSTRUCTOR ===', {
    //   timestamp: new Date().toISOString(),
    //   optionsSuggestionMode: options.suggestionMode,
    //   optionsSuggestionModeType: typeof options.suggestionMode,
    //   calculatedSuggestionMode: this.suggestionMode,
    //   calculationLogic: `options.suggestionMode !== false = ${options.suggestionMode} !== false = ${this.suggestionMode}`,
    //   expectedBehavior: 'suggestionMode should be FALSE for hard deletion',
    //   isWorking: this.suggestionMode === false ? 'YES_HARD_DELETION_ENABLED' : 'NO_STILL_BROKEN'
    // });
  }
  // ❌ REMOVED: Redundant decoder - fixing root cause instead


  validate(context: CommandContext): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if ('from' in this.target) {
      const validation = DocumentUtils.validateNodeId(this.target.from);
      if (!validation.isValid) {
        errors.push(`Invalid 'from' node ID: ${validation.error}`);
      }
    }
    if ('to' in this.target) {
      const validation = DocumentUtils.validateNodeId(this.target.to);
      if (!validation.isValid) {
        errors.push(`Invalid 'to' node ID: ${validation.error}`);
      }
    }
    return { isValid: errors.length === 0, errors };
  }

  execute(context: CommandContext): boolean {
    const { yxml, doc } = context;
    const traceId = (context.metadata as any)?.traceId;
    
    // Replace operation started (verbose logging removed)
    
    traceLog({ traceId, type: 'crdt_op_start', data: { op: 'ReplaceWithMarkdown.execute', target: this.target } });
    
    let fromNode = DocumentUtils.findNodeById(yxml, this.target.from);
    let toNode = DocumentUtils.findNodeById(yxml, this.target.to);

    // Node resolution completed (verbose logging removed)

    if (!fromNode || !toNode || fromNode.parent !== toNode.parent) {
      // Invalid target range - nodes not found or different parents
      traceLog({ traceId, type: 'crdt_op_error', data: { op: 'ReplaceWithMarkdown.execute', stage: 'resolve_targets', message: 'Invalid target range' } });
      
      if (!fromNode || !toNode) {
        return false;
      }
      
      if (fromNode.parent !== toNode.parent) {

        
        // Find common ancestor by walking up the tree
        const findCommonAncestor = (node1: any, node2: any) => {
          
          // ✅ SIMPLIFIED: If one node is at fragment level and other is nested, 
          // expand to include the top-level elements that contain both
          if (node1.parent instanceof Y.XmlFragment && node2.parent instanceof Y.XmlElement) {
            // node1 is at fragment level, node2 is nested
            // Find the top-level element that contains node2
            let topLevelElement = node2.parent;
            let topLevelIndex = -1;
            
            // Walk up until we find an element that's directly under the fragment
            while (topLevelElement && !(topLevelElement.parent instanceof Y.XmlFragment)) {
              topLevelElement = topLevelElement.parent;
            }
            
            // Find the index of this top-level element in the fragment
            if (topLevelElement) {
              for (let i = 0; i < yxml.length; i++) {
                if (yxml.get(i) === topLevelElement) {
                  topLevelIndex = i;
                  break;
                }
              }
            }
            
            if (topLevelIndex >= 0) {
              const normalizedToNode = { element: topLevelElement, index: topLevelIndex, parent: yxml };
              return { from: node1, to: normalizedToNode };
            }
          } else if (node2.parent instanceof Y.XmlFragment && node1.parent instanceof Y.XmlElement) {
            // node2 is at fragment level, node1 is nested (reverse case)
            let topLevelElement = node1.parent;
            let topLevelIndex = -1;
            
            while (topLevelElement && !(topLevelElement.parent instanceof Y.XmlFragment)) {
              topLevelElement = topLevelElement.parent;
            }
            
            if (topLevelElement) {
              for (let i = 0; i < yxml.length; i++) {
                if (yxml.get(i) === topLevelElement) {
                  topLevelIndex = i;
                  break;
                }
              }
            }
            
            if (topLevelIndex >= 0) {
              const normalizedFromNode = { element: topLevelElement, index: topLevelIndex, parent: yxml };
              return { from: normalizedFromNode, to: node2 };
            }
          } else if (node1.parent instanceof Y.XmlElement && node2.parent instanceof Y.XmlElement) {
            // Both nodes are nested - find their top-level parents
            const findTopLevelParent = (node: any) => {
              let current = node.parent;
              while (current && !(current.parent instanceof Y.XmlFragment)) {
                current = current.parent;
              }
              
              if (current) {
                // Find index in fragment
                for (let i = 0; i < yxml.length; i++) {
                  if (yxml.get(i) === current) {
                    return { element: current, index: i, parent: yxml };
                  }
                }
              }
              return null;
            };
            
            const topLevel1 = findTopLevelParent(node1);
            const topLevel2 = findTopLevelParent(node2);
            
            if (topLevel1 && topLevel2) {
              return { from: topLevel1, to: topLevel2 };
            }
          }
          
          // Fallback: If both nodes exist but don't match our scenarios, 
          // try to expand to include the entire range at fragment level
          const findFragmentLevelRange = () => {
            const allFragmentElements: any[] = [];
            
            // Collect all top-level elements and their indices
            for (let i = 0; i < yxml.length; i++) {
              const element = yxml.get(i);
              if (element instanceof Y.XmlElement) {
                allFragmentElements.push({
                  element,
                  index: i,
                  parent: yxml,
                  id: element.getAttribute?.('id')
                });
              }
            }
            
            // Find the range that encompasses both target nodes
            let minIndex = Infinity;
            let maxIndex = -1;
            
            // Check if either node is directly at fragment level
            const node1AtFragment = allFragmentElements.find(e => e.id === node1.element?.getAttribute?.('id'));
            const node2AtFragment = allFragmentElements.find(e => e.id === node2.element?.getAttribute?.('id'));
            
            if (node1AtFragment) {
              minIndex = Math.min(minIndex, node1AtFragment.index);
              maxIndex = Math.max(maxIndex, node1AtFragment.index);
            }
            
            if (node2AtFragment) {
              minIndex = Math.min(minIndex, node2AtFragment.index);
              maxIndex = Math.max(maxIndex, node2AtFragment.index);
            }
            
            // If nodes are nested, find their containing top-level elements
            if (!node1AtFragment || !node2AtFragment) {
              // For nested nodes, find which top-level elements contain them
              const findContainingElement = (targetNode: any) => {
                for (const fragElement of allFragmentElements) {
                  // Check if this fragment element contains our target node
                  const containsNode = (element: any, targetId: string): boolean => {
                    if (element.getAttribute?.('id') === targetId) return true;
                    
                    // Recursively check children
                    for (let i = 0; i < element.length; i++) {
                      const child = element.get(i);
                      if (child instanceof Y.XmlElement && containsNode(child, targetId)) {
                        return true;
                      }
                    }
                    return false;
                  };
                  
                  if (containsNode(fragElement.element, targetNode.element?.getAttribute?.('id'))) {
                    return fragElement;
                  }
                }
                return null;
              };
              
              if (!node1AtFragment) {
                const container1 = findContainingElement(node1);
                if (container1) {
                  minIndex = Math.min(minIndex, container1.index);
                  maxIndex = Math.max(maxIndex, container1.index);
                }
              }
              
              if (!node2AtFragment) {
                const container2 = findContainingElement(node2);
                if (container2) {
                  minIndex = Math.min(minIndex, container2.index);
                  maxIndex = Math.max(maxIndex, container2.index);
                }
              }
            }
            
            if (minIndex !== Infinity && maxIndex !== -1) {
              return {
                from: allFragmentElements[minIndex],
                to: allFragmentElements[maxIndex]
              };
            }
            
            return null;
          };
          
          const fallbackRange = findFragmentLevelRange();
          if (fallbackRange) {
            return fallbackRange;
          }
          
          return null;
        };
        
        const commonRange = findCommonAncestor(fromNode, toNode);
        
        if (commonRange) {
          // Use the normalized range
          fromNode = commonRange.from;
          toNode = commonRange.to;
          
          // Verify they now have the same parent
          if (fromNode?.parent !== toNode?.parent) {
            return false;
          }
          
        } else {
          return false;
        }
      } else {
        return false;
      }
    }

    // ✅ ENHANCED: Add null safety check after normalization
    if (!fromNode || !toNode) {
      return false;
    }

    const parentRef = fromNode.parent;
    let minIndex = Math.min(fromNode.index, toNode.index);
    let maxIndex = Math.max(fromNode.index, toNode.index);

    // Strict targeting: if restrictIds provided, adjust indices to match the exact ids and
    // ensure we only mark deletions on those ids (prevents adjacent node marking)
    let restrictSet = new Set(this.restrictIds || []);
    
    // 🚨 CRITICAL FIX: Detect full document replacement scenario
    const isFullDocumentReplacement = this.batchDescription?.includes('clean document structure') || 
                                      this.batchDescription?.includes('AI content generation');
    
    // 🚨 CRITICAL FIX: For full document replacement, DISABLE restrictSet to allow ALL nodes to be deleted
    if (isFullDocumentReplacement && restrictSet.size === 0) {
      // Keep restrictSet empty to allow ALL nodes in range to be deleted
    } else if (restrictSet.size === 0 && this.target.from === this.target.to) {
      // If caller didn't provide restrictIds but this is a single-node replacement,
      // default to strict mode for the single target id to prevent neighbor bleed.
      restrictSet = new Set([this.target.from]);
    }
    traceLog({ traceId, type: 'strict_mode_init', data: { restrictIds: Array.from(restrictSet), singleTarget: this.target.from === this.target.to } });
    if (restrictSet.size > 0) {
      // If single-node target but id mismatch at minIndex, try to find exact match in parent
      if (minIndex === maxIndex) {
        const childAt = parentRef.get(minIndex) as any;
        const childId = (childAt && typeof childAt.getAttribute === 'function') ? String(childAt.getAttribute('id') || '') : '';
        const wantedId = String(Array.from(restrictSet)[0] || '');
        if (childId !== wantedId) {
          for (let i = 0; i < parentRef.length; i++) {
            const el = parentRef.get(i);
            if (el instanceof Y.XmlElement && el.getAttribute('id') === wantedId) {
              minIndex = i;
              maxIndex = i;
              break;
            }
          }
          // If we still didn't find the exact target, abort to avoid unintended markings
          const verifyEl = parentRef.get(minIndex);
          const verifyId = (verifyEl instanceof Y.XmlElement) ? String(verifyEl.getAttribute('id') || '') : '';
          if (verifyId !== wantedId) {
            traceLog({ traceId, type: 'crdt_op_error', data: { op: 'ReplaceWithMarkdown.execute', stage: 'strict_target_verify', message: 'Target id not found at resolved index', wantedId, resolvedId: verifyId } });
            return false;
          }
        }
      }
    }

    // Derive effective default font for inserted content
    try {
      let effectiveFont: string | undefined;
      try {
        const docStyleMap = context.doc.getMap('doc_style');
        const fam = docStyleMap?.get('fontFamily');
        if (typeof fam === 'string' && fam.trim().length > 0) {
          effectiveFont = fam;
        }
      } catch {}
      if (!effectiveFont) {
        try {
          const fromLoc = DocumentUtils.findNodeById(context.yxml, this.target.from as any);
          const styleId = fromLoc?.element?.getAttribute?.('style_id');
          if (styleId) {
            const registry = StyleService.read(context.doc);
            const paraStyle = (registry.paragraph || {})[styleId];
            const fam = paraStyle?.fontFamily;
            if (typeof fam === 'string' && fam.trim().length > 0) {
              effectiveFont = fam;
            }
          }
        } catch {}
      }
      this.defaultFontFamily = effectiveFont;
    } catch {}

    // Use single normalization pass to prevent over-cleaning
    // Step 1: Strip XML tags that AI might emit (fontFamily, etc.)
    const strippedMarkdown = String(this.markdown)
      .replace(/<fontFamily[^>]*>/g, '')
      .replace(/<\/fontFamily>/g, '')
      .replace(/<fontSize[^>]*>/g, '')
      .replace(/<\/fontSize>/g, '')
      .replace(/<textColor[^>]*>/g, '')
      .replace(/<\/textColor>/g, '')
      .replace(/<backgroundColor[^>]*>/g, '')
      .replace(/<\/backgroundColor>/g, '');
    
    // Step 1.5: Normalize Unicode bullets to standard markdown bullets
    // This was in the old normalizeMarkdown() and must be preserved
    const bulletChars = ['•', '‣', '●', '∙'];
    const unicodeNormalized = strippedMarkdown
      .split(/\r?\n/)
      .map(line => {
        const trimmed = line.trimStart();
        for (const ch of bulletChars) {
          if (trimmed.startsWith(`${ch} `)) {
            const leadingSpaces = line.length - line.trimStart().length;
            return `${' '.repeat(leadingSpaces)}- ${trimmed.slice(2)}`;
          }
        }
        return line;
      })
      .join('\n');
    
    // Step 2: Normalize whitespace (single pass with MarkdownNormalizer)
    const { cleaned: cleanedMarkdown, issues, stats } = MarkdownNormalizer.normalize(unicodeNormalized);
    
    traceLog({ traceId, type: 'content_normalized', data: { 
      length: cleanedMarkdown.length,
      xmlTagsStripped: this.markdown.length !== strippedMarkdown.length,
      normalizationIssues: issues.length,
      stats
    }});
    
    // 🔧 FIX: marked.lexer requires trailing newline to parse block-level elements correctly
    // Without it, "## Heading" is treated as paragraph text instead of a heading token
    const markdownForLexer = cleanedMarkdown.endsWith('\n') ? cleanedMarkdown : cleanedMarkdown + '\n';
    const tokens = marked.lexer(markdownForLexer);
    
    // Filter empty tokens to prevent empty nodes (matches INSERT behavior)
    const { filtered: filteredTokens, removedCount, removedTypes } = MarkdownNormalizer.filterEmptyTokens(tokens);
    
    // 🚨 DEBUGGING: Log the exact markdown and tokens being processed
    // log.log('🔍 [PIPELINE_STAGE_5] === MARKDOWN TOKENIZATION DEBUG ===', {
    //   originalMarkdown: this.markdown,
    //   normalizedMarkdown: normalized,
    //   exactMarkdownBytes: JSON.stringify(cleanedMarkdown),
    //    tokenCount: tokens.length,
    //   tokenTypes: tokens.map((t: any) => t.type),
    //    hasListTokens: tokens.some((t: any) => t.type === 'list'),
    //   listTokens: tokens.filter((t: any) => t.type === 'list'),
    //   allTokensPreview: JSON.stringify(tokens, null, 2).substring(0, 1000),
    //   pipelineStage: 'marked_lexer_tokenization'
    // });
    
    // 🚨 CRITICAL DEBUG: Test marked.lexer with simple bullet point examples
    const testCases = [
      "- First item\n- Second item",
      "* First item\n* Second item",
      "+ First item\n+ Second item",
      "1. First item\n2. Second item",
      cleanedMarkdown
    ];

    // log.log('🔍 [PIPELINE_STAGE_5B] === TESTING MARKED.LEXER WITH VARIOUS INPUTS ===', {
    //   testCaseCount: testCases.length,
    //   pipelineStage: 'marked_lexer_testing'
    // });
    // testCases.forEach((testCase, index) => {
    //   const testTokens = marked.lexer(testCase);
    //   const hasLists = testTokens.some((t: any) => t.type === 'list');
    //   log.log(`🔍 [PIPELINE_STAGE_5B] Test ${index + 1}:`, {
    //     input: testCase,
    //     inputLength: testCase.length,
    //     tokenCount: testTokens.length,
    //     tokenTypes: testTokens.map((t: any) => t.type),
    //     hasListTokens: hasLists,
    //     listTokenCount: testTokens.filter((t: any) => t.type === 'list').length,
    //     firstToken: testTokens[0] ? { type: testTokens[0].type, raw: testTokens[0].raw?.substring(0, 50) } : null,
    //     isCurrentInput: index === testCases.length - 1
    //   });
    // });
    
    // log.log('🔍 [PIPELINE_STAGE_6] === TOKEN PROCESSING START ===', {
    //   tokenCount: tokens.length,
    //   tokenTypes: tokens.map((t: any) => t.type),
    //   hasListTokens: tokens.some((t: any) => t.type === 'list'),
    //   listTokens: tokens.filter((t: any) => t.type === 'list').map((t: any) => ({
    //     type: t.type,
    //     ordered: t.ordered,
    //     itemCount: t.items?.length || 0,
    //     firstItemText: t.items?.[0]?.text?.substring(0, 50) || 'no text'
    //   })),
    //   pipelineStage: 'token_to_yxml_conversion'
    // });
    
    // PRE-TRANSACTION VALIDATION: Validate markdown conversion BEFORE starting transaction to prevent data loss
    let newElements: Y.XmlElement[];
    try {
      newElements = this.markdownTokensToYXml(filteredTokens, context);
      
      // Validate we got valid elements
      if (!newElements || newElements.length === 0) {
        console.error('[ReplaceWithMarkdown] ❌ No elements created from markdown');
        traceLog({ traceId, type: 'crdt_op_error', data: { 
          op: 'ReplaceWithMarkdown', 
          error: 'No elements created',
          markdownLength: this.markdown.length
        }});
        return false;  // FAIL FAST - don't start transaction
      }
      
      // Validate all elements are Y.XmlElements
      const invalidElements = newElements.filter(el => !(el instanceof Y.XmlElement));
      if (invalidElements.length > 0) {
        console.error('[ReplaceWithMarkdown] ❌ Invalid elements created:', {
          invalidCount: invalidElements.length,
          totalCount: newElements.length
        });
        traceLog({ traceId, type: 'crdt_op_error', data: { 
          op: 'ReplaceWithMarkdown', 
          error: `${invalidElements.length} invalid elements`
        }});
        return false;  // FAIL FAST - don't start transaction
      }
      
    } catch (error) {
      console.error('[ReplaceWithMarkdown] ❌ Failed to create elements before transaction:', error);
      traceLog({ traceId, type: 'crdt_op_error', data: { 
        op: 'ReplaceWithMarkdown', 
        error: error instanceof Error ? error.message : String(error),
        markdownPreview: this.markdown.substring(0, 100)
      }});
      return false;  // FAIL FAST - don't start transaction
    }

    // Deterministic behavior: do not auto-expand the span.
    // Only the exact [from, to] range provided will be affected.

    // ✅ NOW SAFE: All validation passed, transaction will succeed
    doc.transact(() => {
      // ✅ OPTIMIZATION: Clear node cache since document is being modified
      yDocumentService.clearNodeCache(doc?.guid);
      
      // log.log('🚨 [Y.JS_DEBUG] === Y.JS TRANSACTION START ===', {
      //   timestamp: new Date().toISOString(),
      //   aboutToDelete: maxIndex - minIndex + 1,
      //   aboutToInsert: newElements.length,
      //   parentChildCountBefore: parentRef.length,
      //   insertionWillStartAt: minIndex
      // });
      
      traceLog({ traceId, type: 'crdt_tx_begin', data: { minIndex, maxIndex } });
      // Mark old nodes for deletion (strictly limited to restrictIds if provided)
      const oldTextAggregate: string[] = [];
      const nodesToHardDelete: number[] = []; // Track indices of nodes to hard delete
      
          for (let i = minIndex; i <= maxIndex; i++) {
      const child = parentRef.get(i);
      // log.log(`🚨 [Y.JS_DEBUG] Processing node ${i}:`, {
      //   nodeIndex: i,
      //   isXmlElement: child instanceof Y.XmlElement,
      //   nodeType: child?.constructor?.name,
      //   childId: child instanceof Y.XmlElement ? child.getAttribute('id') : 'not-xml-element'
      // });
      
      if (child instanceof Y.XmlElement) {
        const childId = child.getAttribute('id') as string;
        // log.log(`🚨 [Y.JS_DEBUG] Found XML element at ${i}:`, {
        //   childId,
        //   restrictSetSize: restrictSet.size,
        //   restrictSetHasId: restrictSet.has(childId),
        //   willProcess: restrictSet.size === 0 || restrictSet.has(childId)
        // });
        
        // If restrictIds is set, only mark/delete nodes with matching IDs
        // log.log(`🚨 [Y.JS_DEBUG] === CHECKING NODE ${i} FOR DELETION ===`, {
        //   nodeIndex: i,
        //   childId,
        //   restrictSetSize: restrictSet.size,
        //   restrictSetContains: restrictSet.has(childId),
        //   restrictSetValues: Array.from(restrictSet),
        //   willProcess: restrictSet.size === 0 || restrictSet.has(childId),
        //   suggestionMode: this.suggestionMode
        // });
        
        if (restrictSet.size === 0 || restrictSet.has(childId)) {
          if (this.suggestionMode) {
            // ✅ ENHANCED: Handle replacement of pending insertions (green suggestions)
            // If we're replacing a node that's already marked as 'new', delete it instead of marking as old
            const isPendingInsertion = String(child.getAttribute('change_status') || '') === 'new';
            if (isPendingInsertion) {
              // This is a green suggestion being replaced - delete it entirely
              nodesToHardDelete.push(i);
            } else {
              // This is original content - mark as old (red)
              // log.log(`📝 [Y.JS_DEBUG] MARKING original content as old: ${childId}`);
              PatchLogger.applyDualMarking(child, 'delete', this.revisionId, true);
            }
          } else {
            nodesToHardDelete.push(i);
          }
          
          // Capture old text for revision history
          try {
            const childText = child.firstChild?.toString?.() || '';
            if (childText.length) oldTextAggregate.push(childText);
          } catch {}
        } else {
          // log.log(`🚨 [Y.JS_DEBUG] SKIPPED node ${i} (${childId}) - not in restrictIds`);
        }
      }
    }

      // log.log('🚨 [Y.JS_DEBUG] Old nodes processed for deletion:', {
      //   nodesToHardDelete: nodesToHardDelete.length,
      //   restrictedToIds: restrictSet.size,
      //   oldTextCaptured: oldTextAggregate.length,
      //   suggestionModeEnabled: this.suggestionMode,
      //   willActuallyDelete: !this.suggestionMode && nodesToHardDelete.length > 0,
      //   isFullDocReplacement: isFullDocumentReplacement,
      //   restrictSetDisabled: isFullDocumentReplacement && restrictSet.size === 0,
      //   criticalFix: isFullDocumentReplacement ? 'RESTRICT_SET_DISABLED_FOR_FULL_REPLACEMENT' : 'NORMAL_OPERATION'
      // });

      // log.log('🚨 [Y.JS_DEBUG] === ABOUT TO EXECUTE HARD DELETION ===', {
      //   nodesToHardDeleteCount: nodesToHardDelete.length,
      //   nodesToHardDeleteIndices: nodesToHardDelete,
      //   suggestionModeActive: this.suggestionMode,
      //   willSkipDeletion: this.suggestionMode,
      //   parentRefLength: parentRef.length,
      //   aboutToSortAndDelete: nodesToHardDelete.length > 0
      // });

      let deletedCount = 0;
      nodesToHardDelete.sort((a, b) => b - a).forEach(index => {
        // log.log(`🚨 [Y.JS_DEBUG] Attempting to delete node at index ${index}:`, {
        //   nodeIndex: index,
        //   parentLength: parentRef.length,
        //   nodeExists: index < parentRef.length,
        //   about: 'hard deletion attempt'
        // });
        
        try {
          parentRef.delete(index, 1);
          deletedCount++;
          // log.log(`✅ [Y.JS_DEBUG] Successfully deleted node at index ${index}`);
          traceLog({ traceId, type: 'hard_delete_executed', data: { index } });
        } catch (e) {
          // log.log('🚨 [Y.JS_DEBUG] Delete failed at index:', index, e);
          traceLog({ traceId, type: 'hard_delete_failed', data: { index, error: String(e) } });
        }
      });
      
      // log.log('🚨 [Y.JS_DEBUG] Deletion complete:', {
      //   deletedCount,
      //   parentChildCountAfterDeletion: parentRef.length,
      //   actuallyDeleted: deletedCount > 0,
      //   suggestionModeWas: this.suggestionMode,
      //   isFullDocReplacement: isFullDocumentReplacement,
      //   restrictSetWasEmpty: restrictSet.size === 0,
      //   fixWorking: (!this.suggestionMode && deletedCount > 0) ? 'YES_HARD_DELETION_ENABLED' : 'NO_STILL_BROKEN'
      // });
      
      // Simply use whatever Claude provided - no hacky heuristics or "smart" reconstruction
      // log.log('🔍 [LOSSLESS_CONVERSION] Processing Claude\'s response as-is:', {
      //   newElementsCount: newElements.length,
      //   newElementTypes: newElements.map(el => el instanceof Y.XmlElement ? el.nodeName : 'unknown'),
      //   markdownReceived: this.markdown.substring(0, 200)
      // });
      
      // Insert new elements - FIXED: For replacement, insert at original position (minIndex), not after (maxIndex + 1)
      const insertionIndex = deletedCount > 0 ? Math.max(0, minIndex) : Math.max(0, minIndex);
      
      // log.log('🚨 [Y.JS_DEBUG] === INSERTION INDEX CALCULATION ===', {
      //   originalMinIndex: minIndex,
      //   originalMaxIndex: maxIndex,
      //   deletedCount,
      //   calculatedInsertionIndex: insertionIndex,
      //   insertionLogic: deletedCount > 0 ? 'replacement_at_minIndex' : 'minimal_document_at_minIndex',
      //   isPage1Insertion: insertionIndex === 0
      // });
      
      // log.log('🚨 [Y.JS_DEBUG] === ABOUT TO INSERT NEW ELEMENTS ===', {
      //   insertionIndex,
      //   elementsToInsert: newElements.length,
      //   adjustedForDeletions: deletedCount,
      //   originalMaxIndex: maxIndex,
      //   willInsertOnPage: insertionIndex === 0 ? 'PAGE_1' : 'PAGE_2+'
      // });
      
      try {
        parentRef.insert(insertionIndex, newElements);
        // log.log('🚨 [Y.JS_DEBUG] ✅ Y.JS INSERTION SUCCESSFUL!', {
        //   insertedAt: insertionIndex,
        //   insertedCount: newElements.length,
        //   parentChildCountAfter: parentRef.length
        // });
        
        // ✅ CRITICAL DEBUG: Check what's actually in the document now
        for (let i = 0; i < Math.min(parentRef.length, 10); i++) {
          const child = parentRef.get(i);
          if (child instanceof Y.XmlElement) {
            const childId = child.getAttribute('id');
            const changeStatus = child.getAttribute('change_status');
            // 🚨 CRITICAL FIX: DO NOT use child.toString() in debug logging - causes contamination!
            let textContent = '[debug text extraction disabled to prevent XML contamination]';
            try {
              const anyChild = child as any;
              if (anyChild._content && typeof anyChild._content === 'string') {
                textContent = anyChild._content.substring(0, 100);
              }
            } catch {
              textContent = '[safe debug text extraction failed]';
            }
          }
        }
        
              // 🔧 CRITICAL DEBUG: Track document state immediately after Y.js insertion
      // log.log('🔧 [Y.JS_TRANSACTION_DEBUG] === POST-INSERTION DOCUMENT STATE ===', {
      //   timestamp: new Date().toISOString(),
      //   yxmlChildCount: parentRef.length,
      //   yxmlChildren: Array.from({length: parentRef.length}, (_, i) => {  // 🚨 SHOW ALL ELEMENTS, not just 10!
      //     const child = parentRef.get(i);
      //     return {
      //       index: i,
      //       type: child instanceof Y.XmlElement ? (child.nodeName || 'element') : 'text',
      //       id: child instanceof Y.XmlElement ? (child.getAttribute('id') || 'no-id') : 'text-node',
      //       // 🚨 CRITICAL FIX: DO NOT use toString() in debug logging - causes contamination!
      //       textLength: child instanceof Y.XmlElement ? '[length hidden - toString() disabled]' : '[text length hidden]',
      //       // 🚨 CRITICAL: Check if any element has page type
      //       isPageNode: child instanceof Y.XmlElement ? child.nodeName === 'page' : false
      //     };
      //   }).slice(0, 50), // Cap at 50 for readability
      //   transactionJustCompleted: true,
      //   aboutToSyncToProseMirror: true,
      //   // 🚨 CRITICAL: Count page nodes in Y.js
      //   pageNodesInYjs: Array.from({length: parentRef.length}, (_, i) => {
      //     const child = parentRef.get(i);
      //     return child instanceof Y.XmlElement && child.nodeName === 'page';
      //   }).filter(Boolean).length
      // });
        
      } catch (insertError) {
        // log.log('🚨 [Y.JS_DEBUG] ❌ Y.JS INSERTION FAILED!', {
        //   insertionIndex,
        //   error: insertError,
        //   parentRef: !!parentRef,
        //   newElementsValid: newElements.every(el => !!el)
        // });
        throw insertError;
      }
      
              this.insertedElements = newElements;
        
        // 🔍 CRITICAL DEBUG: Log what we're storing for getInsertedNodes
        // log.log('🔍 [REPLACE_DEBUG] === STORING INSERTED ELEMENTS ===', {
        //   newElementsCount: newElements.length,
        //   newElementsIds: newElements.map(el => el.getAttribute?.('id')),
        //   insertedElementsCount: this.insertedElements.length,
        //   willReturnFromGetInsertedNodes: this.insertedElements.length > 0
        // });
      
      // Use reusable cleanup function for consistent processing
      this.applyPostInsertCleanup(
        newElements,
        context,
        oldTextAggregate,
        this.suggestionMode,
        this.revisionId,
        this.batchDescription
      );
      
      traceLog({ traceId, type: 'crdt_tx_end', data: { inserted: newElements.length } });
    }, yDocLLMApiOrigin);
    
    traceLog({ traceId, type: 'crdt_op_end', data: { op: 'ReplaceWithMarkdown.execute', inserted: newElements.length } });
    return true;
  }

  private markdownTokensToYXml(tokens: any[], context: CommandContext): Y.XmlElement[] {
    const elements: Y.XmlElement[] = [];

    // 🚨 DEBUGGING: Log all tokens to understand what we're processing
    // log.log('🔍 [PIPELINE_STAGE_7] === INDIVIDUAL TOKEN PROCESSING ===', {
    //   tokenCount: tokens.length,
    //   tokenTypes: tokens.map(t => t.type),
    //   allTokens: JSON.stringify(tokens, null, 2),
    //   pipelineStage: 'individual_token_processing'
    // });

    for (const token of tokens) {
      // log.log('🔍 [PIPELINE_STAGE_7] Processing token:', {
      //   type: token.type,
      //   hasItems: !!token.items,
      //   itemsLength: token.items?.length || 0,
      //   tokenPreview: JSON.stringify(token, null, 2).substring(0, 200),
      //   hasText: !!token.text,
      //   hasRaw: !!token.raw,
      //   textPreview: token.text?.substring(0, 100) || 'no text',
      //   rawPreview: token.raw?.substring(0, 100) || 'no raw'
      // });


      switch (token.type) {
        case 'heading':
          elements.push(this.createHeadingElement(token, context));
          // console.warn('🚨 [TOKEN_DEBUG] Created heading element');
          break;
        case 'paragraph':
          elements.push(this.createParagraphElement(token, context));
          // console.warn('🚨 [TOKEN_DEBUG] Created paragraph element');
          break;
        case 'list':
          // console.warn('🚨 [TOKEN_DEBUG] === PROCESSING LIST TOKEN ===', {
          //   ordered: token.ordered,
          //   itemCount: token.items?.length || 0,
          //   items: token.items?.map((item: any, index: number) => ({
          //     index,
          //     text: item.text?.substring(0, 50) || 'no text',
          //     hasTokens: !!item.tokens,
          //     tokenCount: item.tokens?.length || 0
          //   })) || []
          // });
          elements.push(this.createListElement(token, context));
          // console.warn('🚨 [TOKEN_DEBUG] Created list element');
          break;
        case 'code':
          // ✅ FIX: Decode HTML entities that marked.js encodes in token.text
          // Same issue as doc_write.ts - marked.js encodes quotes even with sanitize: false
          let codeBlockText = token.text || '';
          try {
            const { HtmlEntityDecoder } = require('../../../../../shared/utils/htmlEntityDecoder');
            const beforeDecode = codeBlockText;
            codeBlockText = HtmlEntityDecoder.decode(codeBlockText, 'yDocLLMAPI_code_block');
            if (codeBlockText !== beforeDecode) {
              console.log('🔧 [yDocLLMAPI] Decoded code block entities:', {
                before: beforeDecode.substring(0, 100),
                after: codeBlockText.substring(0, 100)
              });
            }
          } catch (error) {
            console.warn('⚠️ [yDocLLMAPI] Code block decoder failed:', error);
          }
          
          elements.push(
            DocumentUtils.createElement('code_block', codeBlockText, {
              attrs: { language: token.lang },
            })
          );
          // console.warn('🚨 [TOKEN_DEBUG] Created code block element');
          break;
        case 'table': {
          // GFM table support
          const table = DocumentUtils.createElement('table', '', { docId: context.docId });
          let insertRowIndex = 0;
          
          // 🔧 ENHANCED: Handle complex cell objects with formatting preservation
          const mkCellParagraph = (cellData: any) => {
            const p = DocumentUtils.createElement('paragraph', '', { docId: context.docId });
            
            // 🎯 PRIMARY: Use buildFormattedTextFromTokens for structured content
            if (cellData && typeof cellData === 'object' && cellData.type) {
              // This is a token object (e.g., {type: 'strong', text: 'Feature'})
              // Apply preservation to table cell tokens
              const { tokens: preservedTokens, originalRawContext } = this.applyUniversalPreservation([cellData]);
              
              const textNode = this.buildFormattedTextFromTokens(preservedTokens, context, originalRawContext);
              if (textNode) {
                p.insert(0, [textNode]);
                return p;
              }
            }
            
            // 🎯 SECONDARY: Handle string content
            if (typeof cellData === 'string') {
              const tn = new Y.XmlText();
              tn.insert(0, cellData);
              p.insert(0, [tn]);
              return p;
            }
            
            // 🛡️ FALLBACK: Extract text from complex objects
            if (cellData && typeof cellData === 'object') {
              const cellText = this.extractTextFromUnknownToken(cellData);
              
              if (cellText && cellText !== '[object Object]') {
                const tn = new Y.XmlText();
                tn.insert(0, cellText);
                p.insert(0, [tn]);
                return p;
              } else {
                console.error('[TABLE] Failed to extract cell content, using placeholder');
                const tn = new Y.XmlText();
                tn.insert(0, '[Empty Cell]');
                p.insert(0, [tn]);
                return p;
              }
            }
            
            // 🚨 ULTIMATE FALLBACK: Invalid cell data
            console.error('[TABLE] Invalid cell data type, using placeholder');
            const tn = new Y.XmlText();
            tn.insert(0, '[Invalid Cell]');
            p.insert(0, [tn]);
            return p;
          };
          
          const header = token.header || [];
          if (header.length > 0) {
            const headerRow = DocumentUtils.createElement('table_row', '', { docId: context.docId });
            let ci = 0;
            for (const h of header) {
              const th = DocumentUtils.createElement('table_header', '', { docId: context.docId });
              th.insert(0, [mkCellParagraph(h)]);
              headerRow.insert(ci++, [th]);
            }
            table.insert(insertRowIndex++, [headerRow]);
          }
          for (const row of token.rows || []) {
            const tr = DocumentUtils.createElement('table_row', '', { docId: context.docId });
            let ci = 0;
            for (const cell of row) {
              const tc = DocumentUtils.createElement('table_cell', '', { docId: context.docId });
              tc.insert(0, [mkCellParagraph(cell)]);
              tr.insert(ci++, [tc]);
            }
            table.insert(insertRowIndex++, [tr]);
          }
          elements.push(table);
          // console.warn('🚨 [TOKEN_DEBUG] Created table element');
          break;
        }
        case 'hr':
        case 'thematicBreak': {
          const hr = DocumentUtils.createElement('horizontal_rule', '', { docId: context.docId });
          elements.push(hr);
          // console.warn('🚨 [TOKEN_DEBUG] Created horizontal rule element');
          break;
        }
        case 'image': {
          const image = DocumentUtils.createElement('image', '', {
            docId: context.docId,
            attrs: {
              src: token.href || token.src || '',
              alt: token.text || token.alt || '',
              title: token.title || null
            }
          });
          elements.push(image);
          // console.warn('🚨 [TOKEN_DEBUG] Created image element');
          break;
        }
        case 'blockquote':
          // 🚨 CRITICAL FIX: Support blockquote elements (> text)
          // Blockquotes from marked.lexer have nested tokens in token.tokens
          elements.push(this.createBlockquoteElement(token, context));
          // console.warn('🚨 [TOKEN_DEBUG] Created blockquote element');
          break;
        case 'space':
          // Benign whitespace token — skip without error
          // console.warn('🚨 [TOKEN_DEBUG] Skipped space token');
          break;
        case 'text':
          // 🚨 CRITICAL FIX: Handle standalone text tokens (not inside paragraphs)
          if (token.text && token.text.trim()) {
            // console.warn('🚨 [TOKEN_DEBUG] Converting standalone text to paragraph');
            elements.push(this.createParagraphElement({ text: token.text, tokens: [token] }, context));
          }
          break;
        case 'link':
          // 🚨 CRITICAL FIX: Handle standalone link tokens (block-level links)
          // console.warn('🚨 [TOKEN_DEBUG] Converting standalone link to paragraph');
          const linkText = token.text || token.href || 'Link';
          const linkTokens = [{ type: 'link', text: linkText, href: token.href, tokens: [{ type: 'text', text: linkText }] }];
          elements.push(this.createParagraphElement({ text: linkText, tokens: linkTokens }, context));
          break;
        case 'br':
        case 'break':
          // 🚨 CRITICAL FIX: Handle line breaks - convert to hard break in paragraph
          // console.warn('🚨 [TOKEN_DEBUG] Converting line break to paragraph');
          elements.push(this.createParagraphElement({ text: '\n', tokens: [{ type: 'text', text: '\n' }] }, context));
          break;
        case 'escape':
          // 🚨 CRITICAL FIX: Handle escaped characters - preserve the actual character
          const escapedText = token.text || token.raw || '';
          if (escapedText.trim()) {
            // 🔍 QUOTE TRACE: Log escape token processing (critical for quotes)
            const hasQuotesInEscape = /["']/.test(escapedText);
            if (hasQuotesInEscape) {
              // log.log('🔍 [ESCAPE_TOKEN_TRACE] Escape token contains quotes:', {
              //   escapedText: escapedText,
              //   tokenRaw: token.raw,
              //   tokenText: token.text,
              //   quotesFound: escapedText.match(/["']/g) || []
              // });
            }
            
            // console.warn('🚨 [TOKEN_DEBUG] Converting escaped text to paragraph');
            elements.push(this.createParagraphElement({ text: escapedText, tokens: [{ type: 'text', text: escapedText }] }, context));
          }
          break;
        case 'html':
          // 🚨 CRITICAL FIX: Handle raw HTML - extract text content and preserve as paragraph
          const htmlText = token.text || token.raw || '';
          if (htmlText.trim()) {
            // 🔍 QUOTE TRACE: Check if HTML contains quotes before stripping
            const hasQuotesInHtml = /["']/.test(htmlText);
            if (hasQuotesInHtml) {
              // log.log('🔍 [HTML_STRIP_TRACE] HTML token contains quotes:', {
              //   originalHtml: htmlText.substring(0, 100),
              //   quotesFound: htmlText.match(/["']/g) || []
              // });
            }
            
            // Strip HTML tags but preserve text content
            const textContent = htmlText.replace(/<[^>]*>/g, '').trim();
            
            // 🔍 QUOTE TRACE: Check if quotes survived HTML stripping
            if (hasQuotesInHtml) {
              // log.log('🔍 [HTML_STRIP_TRACE] After HTML tag removal:', {
              //   strippedText: textContent.substring(0, 100),
              //   quotesRemaining: (textContent.match(/["']/g) || []).length,
              //   quotesPreserved: (textContent.match(/["']/g) || []).length > 0
              // });
            }
            
            if (textContent) {
              // console.warn('🚨 [TOKEN_DEBUG] Converting HTML block to paragraph (tags stripped)');
              elements.push(this.createParagraphElement({ text: textContent, tokens: [{ type: 'text', text: textContent }] }, context));
            }
          }
          break;
        case 'def':
          // 🚨 CRITICAL FIX: Handle link definitions - convert to paragraph with link format
          const defText = `[${token.tag || 'link'}]: ${token.href || ''}${token.title ? ' "' + token.title + '"' : ''}`;
          // console.warn('🚨 [TOKEN_DEBUG] Converting link definition to paragraph');
          elements.push(this.createParagraphElement({ text: defText, tokens: [{ type: 'text', text: defText }] }, context));
          break;
        default:
    // 🚨 COMPREHENSIVE LOGGING: Log unknown token types for analysis
    console.warn('🚨 [UNKNOWN_TOKEN] Unhandled token type:', {
      type: token.type,
      hasText: !!token.text,
      hasTokens: !!token.tokens,
      hasRaw: !!token.raw,
      keys: Object.keys(token),
      tokenPreview: token.text?.substring(0, 100) || token.raw?.substring(0, 100) || 'no content'
    });
    
    // 🚨 DEBUGGING: Log ALL tokens to understand what marked.lexer is producing
    // console.warn('🚨 [UNKNOWN_TOKEN] Full token object:', JSON.stringify(token, null, 2));
    
    // 🚨 CRITICAL FIX: Handle unknown token types instead of silently ignoring them
    // This was causing raw markdown to be lost when marked.lexer() returned unexpected tokens
    // Create paragraph as fallback to prevent content loss
    const fallbackText = this.extractTextFromUnknownToken(token);
    if (fallbackText && fallbackText.trim()) {
      console.warn('🚨 [TEXT_PRESERVATION_FALLBACK] Converting unknown token to paragraph:', {
        tokenType: token.type,
        extractedText: fallbackText.substring(0, 100),
        reason: 'prevent_text_loss'
      });
      const fallbackTokens = [{ type: 'text', text: fallbackText }];
      elements.push(this.createParagraphElement({ text: fallbackText, tokens: fallbackTokens }, context));
    } else {
      // 🚨 ULTIMATE SAFETY NET: Create paragraph with token type info to prevent complete content loss
      const safetyText = `[Unknown ${token.type || 'token'}${token.raw ? ': ' + token.raw.substring(0, 50) : ''}]`;
      console.error('🚨 [ULTIMATE_SAFETY_NET] Unknown token has no extractable text - creating safety paragraph:', {
        tokenType: token.type,
        tokenKeys: Object.keys(token),
        safetyText: safetyText.substring(0, 100),
        tokenPreview: JSON.stringify(token, null, 2).substring(0, 200)
      });
      const safetyTokens = [{ type: 'text', text: safetyText }];
      elements.push(this.createParagraphElement({ text: safetyText, tokens: safetyTokens }, context));
    }
    break;
      }
    }
    
    // console.warn('🚨 [TOKEN_DEBUG] === TOKEN PROCESSING COMPLETE ===', {
    //   totalTokensProcessed: tokens.length,
    //   elementsCreated: elements.length,
    //   elementTypes: elements.map(el => el.nodeName || 'unknown'),
    //   listElements: elements.filter(el => el.nodeName === 'bullet_list' || el.nodeName === 'ordered_list').length
    // });
    
    // ✅ VALIDATION: Filter out invalid elements to prevent corruption
    // ⚠️ CRITICAL Y.JS LIFECYCLE RULE:
    // DO NOT call getAttribute(), .length, or .get() on Y.XmlElements 
    // before they are inserted into the document tree!
    // 
    // SAFE operations:  .insert(), .format(), .delete()
    // UNSAFE operations: .getAttribute(), .length (read), .get() (read)
    //
    // Violation causes Y.js state corruption and validation failures.
    const validatedElements = elements.filter((el, idx) => {
      if (!(el instanceof Y.XmlElement)) {
        console.error('❌ [VALIDATION] Invalid element at index', idx, '- not a Y.XmlElement');
        return false;
      }
      
      // ✅ FIX: DO NOT call getAttribute('id') before insertion!
      // Y.js elements must be in document tree before attributes can be read.
      // The element already has ID set via createYElement/DocumentUtils.createElement
      // Trust that the factory functions did their job correctly.
      
      return true;  // Only validate type, not attributes
    });
    
    if (validatedElements.length < elements.length) {
      console.error('❌ [VALIDATION] Filtered out', elements.length - validatedElements.length, 'invalid elements');
    }
    
    // log.log('✅ [VALIDATION] Validated elements:', {
    //   originalCount: elements.length,
    //   validatedCount: validatedElements.length,
    //   allPassed: validatedElements.length === elements.length
    // });
    
    return validatedElements;
  }
  
  private createParagraphElement(token: any, context: CommandContext): Y.XmlElement {
    const paragraph = DocumentUtils.createElement('paragraph', '', { docId: context.docId });
    
    // 🚨 UNIVERSAL FIX: Apply preservation to paragraph tokens
    const { tokens: preservedTokens, originalRawContext } = this.applyUniversalPreservation(
      token.tokens || [{ type: 'text', text: token.text || '' }]
    );
    
    // log.log('🔧 [PARAGRAPH_PRESERVATION] Paragraph token processing:', {
    //   originalTokenCount: (token.tokens || []).length,
    //   preservedTokenCount: preservedTokens.length,
    //   hasOriginalRawContext: !!originalRawContext,
    //   originalRawContextPreview: originalRawContext?.substring(0, 100),
    //   tokenTypes: preservedTokens.map((t: any) => t.type)
    // });
    
    const textNode = this.buildFormattedTextFromTokens(preservedTokens, context, originalRawContext);
    if (textNode) {
      // ✅ Just insert without any Y.js reads (prevents lifecycle violations)
      paragraph.insert(0, [textNode]);
    }
    return paragraph;
  }

  private createHeadingElement(token: any, context: CommandContext): Y.XmlElement {
    const level = Math.max(1, Math.min(6, token.depth || token.level || 1));
    
    // 🔧 PROPER ANCHOR SUPPORT: Extract anchor ID from heading text
    let anchorId: string | undefined;
    let cleanTokens = token.tokens || [{ type: 'text', text: token.text || '' }];
    
    // Check if any token contains anchor syntax {#anchor-id}
    cleanTokens = cleanTokens.map((t: any) => {
      if (t.type === 'text' && t.text) {
        const anchorMatch = t.text.match(/^(.*?)\s*\{#([^}]+)\}\s*$/);
        if (anchorMatch) {
          anchorId = anchorMatch[2]; // Extract the anchor ID
          return { ...t, text: anchorMatch[1].trim() }; // Return clean text
        }
      }
      return t;
    });
    
    // ✅ CRITICAL FIX: For Universal Schema, headings are 'block' with className='heading' and level attribute
    // Create heading with anchor ID if found
    const headingAttrs: any = { 
      level,  // ✅ Universal Schema requires level attribute
      className: 'heading'  // ✅ Universal Schema uses className
    };
    if (anchorId) {
      headingAttrs.id = anchorId; // Set the anchor ID as the element's id attribute
      // log.log('🔗 [ANCHOR_SUPPORT] Created heading with anchor:', {
      //   level,
      //   anchorId,
      //   originalText: token.text?.substring(0, 50),
      //   cleanText: cleanTokens[0]?.text?.substring(0, 50)
      // });
    }
    
    const heading = DocumentUtils.createElement('heading', '', { 
      docId: context.docId, 
      attrs: headingAttrs 
    });
    
    // 🚨 UNIVERSAL FIX: Apply preservation to heading tokens
    const { tokens: preservedTokens, originalRawContext } = this.applyUniversalPreservation(cleanTokens);
    
    // log.log('🔧 [HEADING_PRESERVATION] Heading token processing:', {
    //   headingLevel: level,
    //   originalTokenCount: cleanTokens.length,
    //   preservedTokenCount: preservedTokens.length,
    //   hasOriginalRawContext: !!originalRawContext,
    //   originalRawContextPreview: originalRawContext?.substring(0, 100),
    //   hasAnchorId: !!anchorId,
    //   anchorId: anchorId
    // });
    
    const textNode = this.buildFormattedTextFromTokens(preservedTokens, context, originalRawContext);
    if (textNode) heading.insert(0, [textNode]);
    return heading;
  }

  private createListElement(token: any, context: CommandContext): Y.XmlElement {
    const listType: NodeType = token.ordered ? 'ordered_list' as NodeType : 'bullet_list' as NodeType;
    const listElement = DocumentUtils.createElement(listType, '', { docId: context.docId });
    const items = token.items || [];
    
    // Create list items
    let insertionPosition = 0;
    for (const item of items) {
      // Checkbox/task list support
      let checked: boolean | null = null;
      if (item.task === true) checked = !!item.checked;
      
      // 🚨 PRIMARY FIX: Always use tokens when available, not item.text
      let tokensToProcess = item.tokens;
      let fallbackText = item.text || '';
      
      // Handle checkbox detection from raw text if needed
      const cbMatch = fallbackText && fallbackText.match(/^\s*\[( |x|X)\]\s+(.*)$/);
      if (checked === null && cbMatch) {
        checked = cbMatch[1].toLowerCase() === 'x';
        fallbackText = cbMatch[2];
      }
      
      // 🎯 CORE LOGIC: Use structured tokens when available
      if (tokensToProcess && tokensToProcess.length > 0) {
        // log.log('🔧 [LIST_ITEM_PRIMARY] Using structured tokens for list item:', {
        //   tokenCount: tokensToProcess.length,
        //   tokenTypes: tokensToProcess.map((t: any) => t.type),
        //   hasLinks: tokensToProcess.some((t: any) => t.type === 'link'),
        //   hasFormatting: tokensToProcess.some((t: any) => ['strong', 'em', 'code'].includes(t.type)),
        //   firstTokenPreview: JSON.stringify(tokensToProcess[0], null, 2).substring(0, 100)
        // });

        // 🚨 CRITICAL FIX: Preserve original raw content for markdown detection
        // marked.lexer processes tokens and overwrites the 'raw' field with display text
        // We need to preserve the original markdown syntax for secondary parsing
        tokensToProcess = tokensToProcess.map((token: any) => {
          // log.log('🔍 [PRESERVATION_DEBUG] Checking token for preservation:', {
          //   tokenType: token.type,
          //   rawPreview: token.raw?.substring(0, 100),
          //   textPreview: token.text?.substring(0, 100),
          //   hasRaw: !!token.raw,
          //   hasText: !!token.text,
          //   rawLength: token.raw?.length || 0,
          //   textLength: token.text?.length || 0
          // });

          if (token.type === 'text' && token.raw && token.text) {
            // 🔧 IMPROVED DETECTION: Check if raw contains markdown syntax that text doesn't have
            const rawHasMarkdownLink = token.raw.includes('[') && token.raw.includes('](');
            const rawHasMarkdownFormatting = token.raw.includes('**') || token.raw.includes('`') || token.raw.includes('~~');
            const rawHasMarkdown = rawHasMarkdownLink || rawHasMarkdownFormatting;
            
            // Check if text is missing the markdown syntax (indicating processing happened)
            const textIsMissingUrl = rawHasMarkdownLink && !token.text.includes('http');
            const textIsTruncated = token.raw.length > token.text.length;
            const textIsMissingMarkdown = rawHasMarkdown && !token.text.includes(token.raw);
            
            // log.log('🔍 [PRESERVATION_ANALYSIS] Token preservation analysis:', {
            //   rawHasMarkdownLink,
            //   rawHasMarkdownFormatting,
            //   rawHasMarkdown,
            //   textIsMissingUrl,
            //   textIsTruncated,
            //   textIsMissingMarkdown,
            //   shouldPreserve: rawHasMarkdown && (textIsMissingUrl || textIsTruncated || textIsMissingMarkdown),
            //   rawPreview: token.raw?.substring(0, 50),
            //   textPreview: token.text?.substring(0, 50)
            // });
            
            if (rawHasMarkdown) {
              // 🚨 CRITICAL FIX: Always preserve if raw has markdown, regardless of current text state
              // marked.lexer will process tokens later and strip out markdown, so we must preserve now
              // log.log('🔧 [RAW_PRESERVATION] Preserving original raw content:', {
              //   originalRaw: token.raw?.substring(0, 100),
              //   processedText: token.text?.substring(0, 100),
              //   rawHasMarkdownLink,
              //   rawHasMarkdownFormatting,
              //   textIsMissingUrl,
              //   textIsTruncated,
              //   textIsMissingMarkdown,
              //   rawLength: token.raw?.length,
              //   textLength: token.text?.length,
              //   reason: 'raw_has_markdown_always_preserve'
              // });
              
              // Preserve the original raw content for markdown detection
              return {
                ...token,
                originalRaw: token.raw,  // Store original markdown
                text: token.text        // Keep display text for rendering
              };
            } else {
              // log.log('🔍 [PRESERVATION_SKIPPED] Token does not need preservation:', {
              //   rawHasMarkdown,
              //   textIsMissingUrl,
              //   textIsTruncated,
              //   textIsMissingMarkdown,
              //   rawPreview: token.raw?.substring(0, 50),
              //   textPreview: token.text?.substring(0, 50)
              // });
            }
          } else {
            // log.log('🔍 [PRESERVATION_SKIPPED] Token not eligible for preservation:', {
            //   tokenType: token.type,
            //   hasRaw: !!token.raw,
            //   hasText: !!token.text,
            //   reason: !token.raw ? 'no_raw' : !token.text ? 'no_text' : 'wrong_type'
            // });
          }
          return token;
        });
      } else {
        // 🛡️ FALLBACK: Create text token from raw text
        tokensToProcess = [{ type: 'text', text: fallbackText || '' }];
        console.warn('🛡️ [LIST_ITEM_FALLBACK] No tokens available, using raw text:', {
          rawText: fallbackText?.substring(0, 50) || '[empty]',
          hasItemText: !!item.text,
          itemKeys: Object.keys(item)
        });
      }

      const listItem = DocumentUtils.createElement('list_item', '', {
        docId: context.docId,
        attrs: checked !== null ? { checked } : undefined
      } as any);
      const paragraph = DocumentUtils.createElement('paragraph', '', { docId: context.docId });
      
      // 🎯 CRITICAL: Process tokens to preserve formatting and links
      // Pass the original raw content as context for secondary parsing
      let originalRawContext: string | undefined;
      
      // Check if any token has preserved originalRaw content
      const tokenWithOriginalRaw = tokensToProcess.find((t: any) => t.originalRaw);
      if (tokenWithOriginalRaw) {
        originalRawContext = tokenWithOriginalRaw.originalRaw;
        // log.log('🔧 [CONTEXT_PRESERVATION] Passing original raw context to buildFormattedTextFromTokens:', {
        //   originalRawContext: originalRawContext?.substring(0, 100),
        //   tokenCount: tokensToProcess.length,
        //   tokenTypes: tokensToProcess.map((t: any) => t.type),
        //   preservedTokenIndex: tokensToProcess.indexOf(tokenWithOriginalRaw)
        // });
      }
      
      const textNode = this.buildFormattedTextFromTokens(tokensToProcess, context, originalRawContext);
      
      if (textNode) {
        paragraph.insert(0, [textNode]);
      }
      
      listItem.insert(0, [paragraph]);
      
      // Nested lists within list items
      let nestedInsertPosition = 1; // Start after the paragraph
      for (const t of (item.tokens || [])) {
        if (t.type === 'list') {
          const nested = this.createListElement(t, context);
          listItem.insert(nestedInsertPosition, [nested]);
          nestedInsertPosition++;
        }
      }
      listElement.insert(insertionPosition, [listItem]);
      insertionPosition++;
    }
    
    return listElement;
  }

  private createBlockquoteElement(token: any, context: CommandContext): Y.XmlElement {
    // 🚨 CRITICAL FIX: Support blockquote elements for > text syntax
    // Blockquotes contain nested tokens (paragraphs, lists, etc.)
    const blockquote = DocumentUtils.createElement('blockquote', '', { docId: context.docId });
    
    // Process nested tokens within the blockquote
    if (token.tokens && token.tokens.length > 0) {
      const nestedElements = this.markdownTokensToYXml(token.tokens, context);
      for (let i = 0; i < nestedElements.length; i++) {
        blockquote.insert(i, [nestedElements[i]]);
      }
    } else if (token.text) {
      // Fallback: if no nested tokens, create a paragraph with the text
      const paragraph = DocumentUtils.createElement('paragraph', '', { docId: context.docId });
      const textNode = new Y.XmlText();
      textNode.insert(0, token.text);
      paragraph.insert(0, [textNode]);
      blockquote.insert(0, [paragraph]);
    }
    
    return blockquote;
  }

  /**
   * 🚨 UNIVERSAL PRESERVATION: Apply preservation logic to ANY tokens that might contain markdown
   * This ensures links work EVERYWHERE - paragraphs, headings, table cells, blockquotes, etc.
   */
  private applyUniversalPreservation(tokens: any[]): { tokens: any[], originalRawContext?: string } {
    if (!tokens || tokens.length === 0) {
      return { tokens };
    }

    // Apply the same preservation logic used in list items
    const tokensToProcess = tokens.map((token: any) => {
      if (token.type === 'text' && token.raw && token.text) {
        const rawHasMarkdownLink = token.raw.includes('[') && token.raw.includes('](');
        const rawHasMarkdownFormatting = token.raw.includes('**') || token.raw.includes('`') || token.raw.includes('~~');
        const rawHasMarkdown = rawHasMarkdownLink || rawHasMarkdownFormatting;

        if (rawHasMarkdown) {
          // log.log('🔧 [UNIVERSAL_PRESERVATION] Preserving markdown in token:', {
          //   tokenType: token.type,
          //   originalRaw: token.raw,
          //   processedText: token.text,
          //   rawHasMarkdownLink,
          //   rawHasMarkdownFormatting,
          //   context: 'universal_token_processing'
          // });
          return {
            ...token,
            originalRaw: token.raw
          };
        }
      }
      return token;
    });

    // Find any token with preserved content to use as context
    const tokenWithOriginalRaw = tokensToProcess.find((t: any) => t.originalRaw);
    const originalRawContext = tokenWithOriginalRaw?.originalRaw;

    if (originalRawContext) {
      // log.log('🔧 [UNIVERSAL_CONTEXT] Applying universal context preservation:', {
      //   originalRawContext: originalRawContext?.substring(0, 100),
      //   tokenCount: tokensToProcess.length,
      //   preservedTokenIndex: tokensToProcess.indexOf(tokenWithOriginalRaw),
      //   context: 'universal_token_processing'
      // });
    }

    return { tokens: tokensToProcess, originalRawContext };
  }

  private processReparsedTokensIsolated(tokens: any[], activeMarks: Record<string, any>, defaultFont?: string): { text: string; marks?: Record<string, any> }[] {
    const parts: { text: string; marks?: Record<string, any> }[] = [];
    
    const processToken = (token: any, marks: Record<string, any>, depth: number = 0) => {
      // 🚨 RECURSION PROTECTION: Prevent infinite loops in re-parsed token processing
      if (depth > 20) {
        console.error('🚨 [RECURSION_PROTECTION] Maximum recursion depth reached in processReparsedTokensIsolated:', {
          depth,
          tokenType: token?.type,
          activeMarks: Object.keys(marks)
        });
        return;
      }
      
      switch (token.type) {
        case 'text':
          if (token.text || token.raw) {
            const rawText = token.text || token.raw || '';
            // ✅ CRITICAL FIX: Decode HTML entities IMMEDIATELY
            const decoded = HtmlEntityDecoder.decode(rawText, 'processReparsedTokens');
            
            // 🚨 YJS SAFETY: Ensure we never push undefined text
            if (decoded && decoded.trim()) {
              const partMarks: Record<string, any> = { ...marks };
              if (!partMarks.fontFamily && defaultFont && !partMarks.code) {
                partMarks.fontFamily = defaultFont;
              }
              parts.push({ text: decoded, marks: partMarks });
            }
          }
          break;
        case 'strong':
          const strongTokens = token.tokens || [{ type: 'text', text: token.text }];
          for (const st of strongTokens) {
            processToken(st, { ...marks, strong: true }, depth + 1);
          }
          break;
        case 'em':
        case 'emphasis':
          const emTokens = token.tokens || [{ type: 'text', text: token.text }];
          for (const et of emTokens) {
            processToken(et, { ...marks, em: true }, depth + 1);
          }
          break;
        case 'codespan':
          processToken({ type: 'text', text: String(token.text || '') }, { ...marks, code: true }, depth + 1);
          break;
        case 'link':
          const linkTokens = token.tokens || [{ type: 'text', text: token.text }];
          // log.log('🔗 [LINK_MARK_DEBUG] Processing link token:', {
          //   linkText: token.text,
          //   linkHref: token.href,
          //   linkTokensCount: linkTokens.length,
          //   linkTokensTypes: linkTokens.map((lt: any) => lt.type),
          //   activeMarks: Object.keys(marks)
          // });
          for (const lt of linkTokens) {
            // 🚨 CRITICAL FIX: Use correct ProseMirror link mark structure
            const linkMarks = { 
              ...marks, 
              link: { href: token.href, title: token.title || null } 
            };
            // log.log('🔗 [LINK_MARK_APPLIED] Applying ProseMirror-compatible link mark:', {
            //   text: lt.text,
            //   href: token.href,
            //   title: token.title || null,
            //   allMarks: Object.keys(linkMarks),
            //   linkMarkStructure: linkMarks.link
            // });
            processToken(lt, linkMarks, depth + 1);
          }
          break;
        case 'del':
        case 'strike':
          const strikeTokens = token.tokens || [{ type: 'text', text: token.text }];
          for (const dt of strikeTokens) {
            processToken(dt, { ...marks, del: true }, depth + 1);
          }
          break;
        default:
          // Handle unknown token types safely
          const fallbackText = this.extractTextFromUnknownToken(token);
          if (fallbackText) {
            const decoded = fallbackText || ''; // Entities fixed at proper boundary
            const partMarks: Record<string, any> = { ...marks };
            if (!partMarks.fontFamily && defaultFont && !partMarks.code) {
              partMarks.fontFamily = defaultFont;
            }
            parts.push({ text: decoded, marks: partMarks });
          }
          break;
      }
    };
    
    for (const token of tokens) {
      processToken(token, activeMarks);
    }
    
    return parts;
  }

  private buildFormattedTextFromTokens(tokens: any[], context?: CommandContext, originalRawContext?: string): Y.XmlText | null {
    // ✅ VALIDATION: Check inputs to prevent corruption
    if (!tokens || tokens.length === 0) {
      console.warn('⚠️ [VALIDATION] buildFormattedTextFromTokens called with empty tokens');
      return null;
    }

    if (!Array.isArray(tokens)) {
      console.error('❌ [VALIDATION] tokens is not an array:', typeof tokens);
      return null;
    }
    
    let flatParts: { text: string; marks?: Record<string, any> }[] = [];  // ✅ Changed to let for trimming
    let defaultFont: string | undefined = this.defaultFontFamily;
    
    // 🚨 CRITICAL FIX: Track which contexts have been consumed to prevent duplicate re-parsing
    let contextConsumed = false;
    try {
      if (!defaultFont && context?.doc) {
        const styleMap = context.doc.getMap('doc_style');
        const fam = styleMap.get('fontFamily');
        if (typeof fam === 'string' && fam.trim().length > 0) defaultFont = fam;
      }
    } catch {}
    
    // 🎨 NEW: Stack for tracking nested <span style="..."> tags
    const spanStyleStack: Record<string, any>[] = [];
    
    // 🎨 NEW: Get combined styles from all active spans in the stack
    const getCurrentSpanStyles = (): Record<string, any> => {
      return spanStyleStack.reduce((acc, styles) => ({...acc, ...styles}), {});
    };
    
    // 🎨 HELPER: Parse CSS style string (same as doc_write.ts)
    const parseStyleString = (styleStr: string): Record<string, any> => {
      const marks: Record<string, any> = {};
      const props = styleStr.split(';').map(p => p.trim()).filter(Boolean);
      
      for (const prop of props) {
        const colonIndex = prop.indexOf(':');
        if (colonIndex === -1) continue;
        
        const key = prop.substring(0, colonIndex).trim().toLowerCase();
        const value = prop.substring(colonIndex + 1).trim();
        if (!value) continue;
        
        switch (key) {
          case 'color':
            if (/^(#[0-9a-f]{3,8}|[a-z]+|rgb|rgba|hsl|hsla)/i.test(value)) {
              marks.textColor = { color: value };
            }
            break;
          case 'background-color':
          case 'background':
            if (/^(#[0-9a-f]{3,8}|[a-z]+|rgb|rgba|hsl|hsla)/i.test(value)) {
              marks.backgroundColor = { backgroundColor: value };
            }
            break;
          case 'font-family':
            const cleanFont = value.replace(/^['"]|['"]$/g, '').trim();
            if (cleanFont.length > 0) {
              marks.fontFamily = { font: cleanFont };
            }
            break;
          case 'font-size':
            const sizeMatch = value.match(/^(\d+(?:\.\d+)?)(pt|px|em|rem)?$/i);
            if (sizeMatch) {
              let size = parseFloat(sizeMatch[1]);
              const unit = (sizeMatch[2] || 'pt').toLowerCase();
              if (unit === 'px') size = Math.round(size * 0.75);
              else if (unit === 'em' || unit === 'rem') size = Math.round(size * 12);
              size = Math.max(6, Math.min(72, size));
              marks.fontSize = { size };
            }
            break;
          case 'font-weight':
            const weight = value.toLowerCase();
            const numericWeight = parseInt(value);
            
            // Map to semantic strong mark if bold
            if (weight === 'bold' || weight === 'bolder' || numericWeight >= 600) {
              marks.strong = true;
            }
            
            // Also store full weight value in fontWeight mark
            if (!isNaN(numericWeight) && numericWeight !== 400) {
              marks.fontWeight = { weight: String(numericWeight) };
            } else if (weight && weight !== 'normal' && weight !== 'bold') {
              marks.fontWeight = { weight: value };
            }
            break;
            
          case 'font-style':
            if (value.toLowerCase() === 'italic') {
              marks.em = true;
            }
            break;
            
          case 'text-decoration':
          case 'text-decoration-line':
            const decorValue = value.toLowerCase();
            if (decorValue.includes('underline')) {
              marks.underline = true;
            }
            if (decorValue.includes('line-through')) {
              marks.strike = true;
            }
            break;
            
          case 'vertical-align':
            const alignValue = value.toLowerCase();
            if (alignValue === 'super' || alignValue === 'superscript') {
              marks.superscript = true;
            } else if (alignValue === 'sub' || alignValue === 'subscript') {
              marks.subscript = true;
            }
            break;
        }
      }
      return marks;
    };
    
    // 🎨 HELPER: Detect styled span tags
    const detectStyledSpan = (htmlStr: string): {isOpen: boolean; isClose: boolean; styles: Record<string, any>} => {
      const openMatch = htmlStr.match(/<span\s+style="([^"]+)"[^>]*>/i);
      if (openMatch) {
        return { isOpen: true, isClose: false, styles: parseStyleString(openMatch[1]) };
      }
      if (htmlStr.match(/<\/span>/i)) {
        return { isOpen: false, isClose: true, styles: {} };
      }
      return {isOpen: false, isClose: false, styles: {}};
    };

    const walk = (tks: any[], activeMarks: Record<string, any> = {}, depth: number = 0) => {
      // 🚨 RECURSION PROTECTION: Prevent infinite loops
      if (depth > 50) {
        console.error('🚨 [RECURSION_PROTECTION] Maximum recursion depth reached, stopping walk:', {
          depth,
          tokenCount: tks?.length,
          activeMarks: Object.keys(activeMarks)
        });
        return;
      }
      
      for (const t of tks) {
        switch (t.type) {
          case 'text':
            if (t.tokens && Array.isArray(t.tokens)) {
              // 🚨 RECURSION PROTECTION: Pass depth + 1 but allow legitimate recursion
              if (t.tokens.length > 0 && depth < 50) {
                walk(t.tokens, activeMarks, depth + 1);
              } else if (depth >= 50) {
                console.error('🚨 [RECURSION_PROTECTION] Skipping deep token recursion:', {
                  depth,
                  tokenCount: t.tokens?.length,
                  tokenType: t.type
                });
              }
            } else if (t.text || t.raw || t.originalRaw) {
              // 🚨 ROOT CAUSE FIX: Don't use originalRawContext for markdown detection!
              // Only use individual token content for detection to prevent infinite loops
              const individualTextToCheck = t.originalRaw || t.raw || t.text;
              const displayText = t.text || t.raw;
              
              // 🚨 CRITICAL DEBUG: Log every text token to see what we're processing
              // log.log('🔍 [TEXT_TOKEN_DEBUG] Processing text token:', {
              //   text: displayText?.substring(0, 100),
              //   raw: t.raw?.substring(0, 100),
              //   originalRaw: t.originalRaw?.substring(0, 100),
              //   originalRawContext: originalRawContext?.substring(0, 100),
              //   textLength: displayText?.length || 0,
              //   rawLength: t.raw?.length || 0,
              //   originalRawLength: t.originalRaw?.length || 0,
              //   originalRawContextLength: originalRawContext?.length || 0,
              //   usingOriginalRaw: !!t.originalRaw,
              //   usingOriginalRawContext: !t.originalRaw && !!originalRawContext,
              //   usingRawForDetection: !t.originalRaw && !originalRawContext && !!t.raw,
              //   containsLinks: individualTextToCheck?.includes('[') && individualTextToCheck?.includes(']('),
              //   containsBrackets: individualTextToCheck?.includes('[') && individualTextToCheck?.includes(']'),
              //   containsParens: individualTextToCheck?.includes('(') && individualTextToCheck?.includes(')'),
              //   activeMarks: Object.keys(activeMarks)
              // });
              
              // 🚨 CRITICAL FIX: Secondary markdown parsing for text tokens containing markdown syntax
              // This handles cases where marked.lexer treats "[link](url)" as text instead of parsing it as a link
              // Check individual token first, then originalRawContext as fallback for detection
              // PREVENT DUPLICATE PROCESSING: Only allow first token to consume the context
              const shouldUseContext = originalRawContext && this.containsMarkdownSyntax(originalRawContext) && !contextConsumed;
              if (this.containsMarkdownSyntax(individualTextToCheck) || shouldUseContext) {
                
                // 🚨 CRITICAL FIX: Mark context as consumed to prevent duplicate processing
                if (shouldUseContext) {
                  contextConsumed = true;
                }
                
                // Determine which text to use for re-parsing
                const textToReparse = this.containsMarkdownSyntax(individualTextToCheck) 
                  ? individualTextToCheck 
                  : originalRawContext;
                
                log.log('🔧 [SECONDARY_MARKDOWN_PARSE] Text token contains markdown syntax, re-parsing:', {
                  originalText: displayText?.substring(0, 100),
                  rawText: t.raw?.substring(0, 100),
                  originalRawText: t.originalRaw?.substring(0, 100),
                  originalRawContextText: originalRawContext?.substring(0, 100),
                  individualTextToCheck: individualTextToCheck?.substring(0, 100),
                  textToReparse: textToReparse?.substring(0, 100),
                  usingIndividualText: this.containsMarkdownSyntax(individualTextToCheck),
                  usingOriginalRawContext: shouldUseContext,
                  contextConsumed: contextConsumed,
                  hasLinks: individualTextToCheck?.includes('[') && individualTextToCheck?.includes(']('),
                  hasFormatting: individualTextToCheck?.includes('**') || individualTextToCheck?.includes('*') || individualTextToCheck?.includes('`'),
                  activeMarks: Object.keys(activeMarks)
                });
                
                // Re-parse the raw content as markdown to extract links and formatting
                const reparsedTokens = this.parseInlineMarkdown(textToReparse);
                if (reparsedTokens && reparsedTokens.length > 0) {
                  log.log('🔧 [SECONDARY_PARSE_SUCCESS] Re-parsed tokens:', {
                    originalText: displayText?.substring(0, 50),
                    rawText: t.raw?.substring(0, 50),
                    reparsedCount: reparsedTokens.length,
                    reparsedTypes: reparsedTokens.map((rt: any) => rt.type),
                    hasReparsedLinks: reparsedTokens.some((rt: any) => rt.type === 'link')
                  });
                  // 🚨 CRITICAL FIX: Process re-parsed tokens WITHOUT originalRawContext to prevent infinite recursion
                  // Call a separate method that has NO access to originalRawContext
                  // 🚨 FORMATTING BLEED FIX: Use clean marks for re-parsed content to prevent inheritance
                  const cleanActiveMarks = { 
                    fontFamily: activeMarks.fontFamily // Keep only essential marks
                  };
                  const reparsedParts = this.processReparsedTokensIsolated(reparsedTokens, cleanActiveMarks, defaultFont);
                  log.log('🔗 [REPARSED_PARTS_DEBUG] Adding re-parsed parts with clean marks:', {
                    reparsedPartsCount: reparsedParts.length,
                    originalActiveMarks: Object.keys(activeMarks),
                    cleanActiveMarks: Object.keys(cleanActiveMarks),
                    reparsedParts: reparsedParts.map(rp => ({
                      text: rp.text?.substring(0, 20),
                      marks: Object.keys(rp.marks || {}),
                      hasLinkMark: !!(rp.marks?.link),
                      linkHref: rp.marks?.link?.href || rp.marks?.link
                    })),
                    flatPartsCountBefore: flatParts.length
                  });
                  flatParts.push(...reparsedParts);
                } else {
                  console.warn('🔧 [SECONDARY_PARSE_FAILED] Re-parsing failed, using original text:', {
                    originalText: displayText?.substring(0, 50),
                    rawText: t.raw?.substring(0, 50)
                  });
                  // Fallback to original display text if re-parsing fails
                  const rawText = displayText || '';
                  // ✅ CRITICAL FIX: Decode HTML entities IMMEDIATELY
                  const decoded = HtmlEntityDecoder.decode(rawText, 'fallback-after-reparse');
                  
                  if (decoded && decoded.trim()) {
                    const partMarks: Record<string, any> = { ...activeMarks };
                    if (!partMarks.fontFamily && defaultFont && !partMarks.code) {
                      partMarks.fontFamily = defaultFont;
                    }
                    flatParts.push({ text: decoded, marks: partMarks });
                  }
                }
              } else if (originalRawContext && contextConsumed) {
                log.log('🔍 [TEXT_TOKEN_SKIPPED] Skipping token because context was already consumed by previous token:', {
                  text: displayText?.substring(0, 50),
                  raw: t.raw?.substring(0, 50),
                  originalRawContext: originalRawContext?.substring(0, 50),
                  activeMarksBeforeSkip: Object.keys(activeMarks),
                  reason: 'context_already_consumed_by_previous_token'
                });
                // Skip this token entirely - it's already been processed as part of the re-parsed context
                // 🚨 FORMATTING BLEED FIX: Don't add any content for skipped tokens to prevent mark inheritance
              } else {
                // log.log('🔍 [TEXT_TOKEN_NORMAL] No markdown syntax detected, processing as normal text:', {
                //   text: displayText?.substring(0, 50),
                //   raw: t.raw?.substring(0, 50),
                //   checkedText: individualTextToCheck?.substring(0, 50),
                //   contextConsumed: contextConsumed,
                //   hasOriginalRawContext: !!originalRawContext,
                //   activeMarksInherited: Object.keys(activeMarks),
                //   reason: 'containsMarkdownSyntax_returned_false'
                // });
                // Normal text processing
                const rawText = (displayText || '').trim();
                
                // 🚨 YJS SAFETY: Ensure we never push undefined text
                if (!rawText) {
                  console.warn('🚨 [YJS_SAFETY] Skipping undefined/empty text token:', {
                    originalText: displayText,
                    tokenType: t.type,
                    hasOriginalRaw: !!t.originalRaw,
                    hasRaw: !!t.raw
                  });
                  break;
                }
                
                // ✅ CRITICAL FIX: Decode HTML entities IMMEDIATELY
                const decoded = HtmlEntityDecoder.decode(rawText, 'normal-text-processing');
                
                if (!decoded || !decoded.trim()) {
                  // Edge case: decoding resulted in empty string
                  break;
                }
                
                // 🚨 FORMATTING BLEED FIX: Intelligently inherit marks based on context
                const partMarks: Record<string, any> = {};
                
                // 🎨 NEW: Merge span styles first (from HTML styling)
                const spanStyles = getCurrentSpanStyles();
                Object.assign(partMarks, spanStyles);
                
                // Always inherit fontFamily (essential formatting)
                if (activeMarks.fontFamily) partMarks.fontFamily = activeMarks.fontFamily;
                if (!partMarks.fontFamily && defaultFont && !activeMarks.code) {
                  partMarks.fontFamily = defaultFont;
                }
                
                // 🚨 CRITICAL FIX: Prevent formatting bleed while preserving legitimate formatting
                // The issue: activeMarks can contain formatting from previous siblings
                // The solution: Only inherit formatting marks that are contextually appropriate
                
                // Check if this token should have formatting based on the walk context
                // If we're in a 'strong' case in the walk function, activeMarks.strong is legitimate
                // If we're in default case but have activeMarks.strong, it might be bleed from siblings
                
                const hasLegitimateStrong = activeMarks.strong && (
                  // We're processing re-parsed content (secondary parsing sets clean context)
                  !!originalRawContext ||
                  // OR this is the first token in a sequence (no bleed risk)
                  !contextConsumed
                );
                
                const hasLegitimateEm = activeMarks.em && (!!originalRawContext || !contextConsumed);
                const hasLegitimateCode = activeMarks.code && (!!originalRawContext || !contextConsumed);  
                const hasLegitimateLink = activeMarks.link; // Links are always legitimate
                const hasLegitimateDel = activeMarks.del && (!!originalRawContext || !contextConsumed);  // ~~strikethrough~~
                
                // Apply legitimate formatting marks
                if (hasLegitimateStrong) partMarks.strong = activeMarks.strong;
                if (hasLegitimateEm) partMarks.em = activeMarks.em;
                if (hasLegitimateCode) partMarks.code = activeMarks.code;
                if (hasLegitimateLink) partMarks.link = activeMarks.link;
                if (hasLegitimateDel) partMarks.del = activeMarks.del;
                
                // Log mark inheritance for debugging (reduced logging)
                if (activeMarks.strong || hasLegitimateStrong) {
                  log.log('🔧 [MARK_INHERITANCE_DEBUG] Context-aware mark inheritance:', {
                    text: displayText?.substring(0, 20),
                    hasActiveStrong: !!activeMarks.strong,
                    hasLegitimateStrong,
                    inheritedStrong: !!partMarks.strong
                  });
                }
                
                flatParts.push({ text: decoded, marks: partMarks });
              }
            }
            break;
          case 'strong':
            walk(t.tokens || [{ type: 'text', text: t.text }], { ...activeMarks, strong: true }, depth + 1);
            break;
          case 'em':
          case 'emphasis':
            walk(t.tokens || [{ type: 'text', text: t.text }], { ...activeMarks, em: true }, depth + 1);
            break;
          case 'codespan':
            // ✅ FIX: Decode HTML entities from marked.js encoded text
            // Same as doc_write.ts - must use t.text (not t.raw which has backticks)
            let codespanText = String(t.text || '');
            try {
              const { HtmlEntityDecoder } = require('../../../../../shared/utils/htmlEntityDecoder');
              codespanText = HtmlEntityDecoder.decode(codespanText, 'yDocLLMAPI_inline_code');
            } catch (error) {
              // Fallback to original if decoder unavailable
            }
            walk([{ type: 'text', text: codespanText }], { ...activeMarks, code: true }, depth + 1);
            break;
          case 'link':
            // 🚨 CRITICAL FIX: Use correct ProseMirror link mark structure
            walk(t.tokens || [{ type: 'text', text: t.text }], { 
              ...activeMarks, 
              link: { href: t.href, title: t.title || null } 
            }, depth + 1);
            break;
          case 'del':
          case 'strike':
            // Support for ~~strikethrough~~ text
            walk(t.tokens || [{ type: 'text', text: t.text }], { ...activeMarks, del: true }, depth + 1);
            break;
            
          case 'html':
            // 🎨 NEW: Handle inline HTML for visual styling
            const htmlRaw = t.raw || t.text || '';
            const spanInfo = detectStyledSpan(htmlRaw);
            
            if (spanInfo.isOpen) {
              // Opening styled span - push styles to stack
              spanStyleStack.push(spanInfo.styles);
              log.log('[yDocLLMAPI] 🎨 Opening styled span:', {
                styles: Object.keys(spanInfo.styles),
                stackDepth: spanStyleStack.length
              });
            } else if (spanInfo.isClose) {
              // Closing span - pop from stack
              if (spanStyleStack.length > 0) {
                spanStyleStack.pop();
                log.log('[yDocLLMAPI] 🎨 Closing styled span, depth:', spanStyleStack.length);
              }
            } else {
              // Other HTML - strip tags, preserve text
              const stripped = htmlRaw.replace(/<[^>]*>/g, '');
              if (stripped) {
                const decoded = HtmlEntityDecoder.decode(stripped, 'html-other');
                if (decoded && decoded.trim()) {
                  const spanStyles = getCurrentSpanStyles();
                  const combinedMarks = { ...activeMarks, ...spanStyles };
                  flatParts.push({ text: decoded, marks: combinedMarks });
                }
              }
            }
            break;
            
          default:
            if (t.text) {
              const rawText = (t.text || '').trim();
              // ✅ CRITICAL FIX: Decode HTML entities IMMEDIATELY
              const decoded = HtmlEntityDecoder.decode(rawText, 'default-token-case');
              
              // 🚨 YJS SAFETY: Ensure we never push undefined text
              if (decoded && decoded.trim()) {
                // 🎨 NEW: Merge span styles with active marks
                const spanStyles = getCurrentSpanStyles();
                const combinedMarks = { ...activeMarks, ...spanStyles };
                
                if (!combinedMarks.fontFamily && defaultFont && !combinedMarks.code) {
                  combinedMarks.fontFamily = defaultFont;
                }
                flatParts.push({ text: decoded, marks: combinedMarks });
              }
            }
        }
      }
    };

    walk(tokens);
    
    // ✅ VALIDATION: Check for unclosed styled spans
    if (spanStyleStack.length > 0) {
      console.warn('⚠️ [yDocLLMAPI] [HTML_STYLE] Unclosed styled span tags detected:', {
        unclosedCount: spanStyleStack.length,
        remainingStyles: spanStyleStack.map(s => Object.keys(s)),
        note: 'Styles were applied to available text'
      });
    }

    // ✅ PHASE 3: Trim flatParts BEFORE building combined text to fix offset mismatch
    // This ensures flatParts lengths match the final text exactly
    if (flatParts.length > 0) {
      // Remove empty parts first
      flatParts = flatParts.filter(p => p.text && p.text.length > 0);
      
      if (flatParts.length > 0) {
        // Trim leading whitespace from first part
        const first = flatParts[0];
        const trimmedFirstText = first.text.replace(/^\s+/, '');
        if (trimmedFirstText !== first.text) {
          flatParts[0] = { ...first, text: trimmedFirstText };
        }
        
        // Trim trailing whitespace from last part
        const last = flatParts[flatParts.length - 1];
        const trimmedLastText = last.text.replace(/\s+$/, '');
        if (trimmedLastText !== last.text) {
          flatParts[flatParts.length - 1] = { ...last, text: trimmedLastText };
        }
        
        // Remove any empty parts after trimming
        flatParts = flatParts.filter(p => p.text && p.text.length > 0);
      }
    }

    // log.log('🔗 [YJS_CREATION_DEBUG] Creating YJS text node from flatParts:', {
    //   flatPartsCount: flatParts.length,
    //   flatParts: flatParts.map((fp, i) => ({
    //     index: i,
    //     text: fp.text?.substring(0, 20),
    //     textLength: fp.text?.length || 0,
    //     marks: Object.keys(fp.marks || {}),
    //     hasLinkMark: !!(fp.marks?.link),
    //     linkHref: fp.marks?.link
    //   })),
    //   combinedTextPreview: flatParts.map(p => p.text || '').join('').substring(0, 50)
    // });

    // ✅ No .trim() needed - flatParts are already trimmed
    const combinedText = flatParts.map(p => p.text || '').join('');
    const rawCombined = flatParts.map(p => p.text || '').join('');
    // log.log('🚨 [COMBINED_TEXT_DEBUG]', {
    //   rawLength: rawCombined.length,
    //   trimmedLength: combinedText.length,
    //   rawPreview: rawCombined.substring(0, 100),
    //   trimmedPreview: combinedText.substring(0, 100),
    //   hasOnlyWhitespace: combinedText.length === 0,
    //   flatPartsCount: flatParts.length,
    //   rawEndsWithNewlines: rawCombined.endsWith('\n') ? (rawCombined.match(/\n+$/)?.[0]?.length || 0) + ' newlines' : 'no',
    //   trimmedEndsWithNewlines: combinedText.endsWith('\n') ? (combinedText.match(/\n+$/)?.[0]?.length || 0) + ' newlines' : 'no',
    //   allParts: flatParts.map((p: any) => ({text: (p.text || '').substring(0, 50), length: (p.text || '').length, endsWithNewline: (p.text || '').endsWith('\n')}))
    // });
    
    // 🔍 FINAL Y.JS QUOTE TRACE: Track quotes through entire process
    const hasQuotesForYjs = /["']/.test(combinedText);
    if (hasQuotesForYjs) {
      log.log('🔍 [YJS_FINAL_TRACE] Text about to be inserted into Y.js:', {
        combinedText: combinedText.substring(0, 200),
        quotesFound: combinedText.match(/["']/g) || [],
        entitiesFound: combinedText.match(/&[a-zA-Z0-9#]+;/g) || [],
        totalLength: combinedText.length,
        flatPartsCount: flatParts.length,
        SOURCE: 'finalYjsInsertion'
      });
    }
    
    // ✅ Text already decoded in flatParts - no need to decode again
    // Just validate and use directly
    let decodedText = combinedText;
    
    // 🛡️ SAFETY CHECK: Verify no entities remain (should already be decoded)
    if (HtmlEntityDecoder.hasEntities(decodedText)) {
      console.error('🚨 [ENCODING_BUG] HTML entities found in text that should already be decoded!', {
        text: decodedText.substring(0, 100),
        entities: decodedText.match(/&[a-zA-Z0-9#]+;/g) || [],
        source: 'This indicates entities were not decoded when added to flatParts'
      });
      
      // Emergency decode as safety net (but this means we have a bug elsewhere)
      decodedText = HtmlEntityDecoder.decode(decodedText, 'emergency-safety-decode');
    }
    
    // ✅ VALIDATION: Check text before Y.js insertion
    if (!decodedText || decodedText.length === 0) {
      console.warn('⚠️ [VALIDATION] Empty text, creating empty Y.XmlText');
      return new Y.XmlText();
    }
    
    if (decodedText.length > 1000000) {  // 1MB limit to prevent memory issues
      console.error('❌ [VALIDATION] Text too large:', decodedText.length, 'bytes - truncating to 1MB');
      decodedText = decodedText.substring(0, 1000000);
    }
    
    const textNode = new Y.XmlText();
    // ✅ Track final text length for format bounds checking (no Y.js reads)
    let finalTextLength = 0;
    
    if (decodedText.length > 0) {
      // ✅ No trim needed - flatParts were already trimmed above
      // Insert decodedText directly
      textNode.insert(0, decodedText);
      finalTextLength = decodedText.length;
    }

    // ✅ Calculate expected length without Y.js reads
    const flatPartsTotalLength = flatParts.reduce((sum, p) => sum + (p.text || '').length, 0);
    const expectedTextLength = finalTextLength;
    const willMismatch = expectedTextLength !== flatPartsTotalLength;
    
    // ⚠️ After our fixes, lengths should ALWAYS match
    if (willMismatch) {
      console.error('🚨 [LENGTH_MISMATCH_BUG] CRITICAL: Text lengths do not match!', {
        expectedTextLength,
        flatPartsTotalLength,
        mismatchSize: flatPartsTotalLength - expectedTextLength,
        possibleCause: 'HTML entities not decoded early, or other encoding issue',
        flatPartsPreview: flatParts.map(p => ({
          text: p.text.substring(0, 30),
          length: p.text.length,
          hasEntities: HtmlEntityDecoder.hasEntities(p.text)
        })),
        combinedTextHasEntities: HtmlEntityDecoder.hasEntities(combinedText),
        decodedTextHasEntities: HtmlEntityDecoder.hasEntities(decodedText)
      });
      
      // ❌ DO NOT CONTINUE - this will corrupt the document
      // Return text node WITHOUT formatting to prevent bounds errors
      console.error('🚨 [SAFETY] Returning text node without formatting to prevent corruption');
      return textNode;
    }

    let offset = 0;
    for (const part of flatParts) {
      const partText = part.text || ''; // Entities fixed at proper boundary
      const length = partText.length;
      if (length > 0 && part.marks && Object.keys(part.marks).length > 0) {
        // 🚨 CRITICAL FIX: Filter out undefined values from marks to prevent YJS serialization errors
        const safeMarks: Record<string, any> = {};
        for (const [key, value] of Object.entries(part.marks)) {
          if (value !== undefined && value !== null) {
            safeMarks[key] = value;
          }
        }
        
        if (Object.keys(safeMarks).length > 0) {
          const formatEnd = offset + length;
          const exceedsBounds = formatEnd > finalTextLength;  // ✅ Use tracked length, no Y.js read
          
          log.log('🔗 [YJS_FORMAT_DEBUG] Applying marks to YJS text:', {
            offset: offset,
            length: length,
            text: partText.substring(0, 20),
            marks: Object.keys(safeMarks),
            hasLinkMark: !!(safeMarks.link),
            linkHref: safeMarks.link,
            expectedTextLength: finalTextLength,
            formatEnd: formatEnd,
            exceedsBounds: exceedsBounds,
            filteredOutUndefined: Object.keys(part.marks || {}).filter(k => (part.marks || {})[k] === undefined || (part.marks || {})[k] === null)
          });
          
          if (!exceedsBounds) {
            try {
              textNode.format(offset, length, safeMarks);
            } catch (error) {
              console.error('🚨 [FORMAT_ERROR] Failed to apply formatting:', {
                offset,
                length,
                marks: Object.keys(safeMarks),
                error: error instanceof Error ? error.message : String(error),
                partText: partText.substring(0, 50)
              });
              // Continue - don't let formatting failure break the entire operation
            }
          } else {
            console.error('🚨 [YJS_FORMAT_BOUNDS_ERROR] SKIPPING FORMAT - WOULD EXCEED TEXT BOUNDS!', {
              offset,
              length,
              formatEnd,
              expectedTextLength: finalTextLength,
              partIndex: flatParts.indexOf(part),
              failedMark: safeMarks,
              criticalError: 'This should NEVER happen after our fixes - indicates a bug'
            });
            // ⚠️ After our fixes, this should NEVER execute
            // If it does, we have a serious bug that needs investigation
          }
        }
      }
      offset += length;
    }

    // ✅ Return textNode without any reads (prevents Y.js lifecycle violations)
    return textNode;
  }

  private normalizeMarkdown(markdown: string): string {
    if (!markdown) return markdown;
    
    // 🚨 CRITICAL DEBUG: Log the normalization process step by step
    console.warn('🚨 [NORMALIZE_DEBUG] === MARKDOWN NORMALIZATION PROCESS ===', {
      originalMarkdown: markdown,
      originalLength: markdown.length,
      originalLines: markdown.split(/\r?\n/),
      containsBullets: markdown.includes('-') || markdown.includes('*') || markdown.includes('+'),
      containsNumbers: /^\d+\.\s/.test(markdown),
      step: 'START_NORMALIZATION'
    });
    
    // Strip any font-related pseudo-tags that the model might have emitted
    const stripped = String(markdown)
      .replace(/<fontFamily[^>]*>/g, '')
      .replace(/<\/fontFamily>/g, '')
      .replace(/<fontSize[^>]*>/g, '')
      .replace(/<\/fontSize>/g, '')
      .replace(/<textColor[^>]*>/g, '')
      .replace(/<\/textColor>/g, '')
      .replace(/<backgroundColor[^>]*>/g, '')
      .replace(/<\/backgroundColor>/g, '');
      
    console.warn('🚨 [NORMALIZE_DEBUG] After HTML tag stripping:', {
      strippedMarkdown: stripped,
      strippedLength: stripped.length,
      htmlTagsRemoved: stripped !== markdown,
      step: 'HTML_TAGS_STRIPPED'
    });
    
    const lines = stripped.split(/\r?\n/);
    const bulletChars = ['•', '‣', '●', '∙'];
    
    console.warn('🚨 [NORMALIZE_DEBUG] Processing lines for bullet normalization:', {
      totalLines: lines.length,
      linesWithBullets: lines.filter(line => line.includes('-') || line.includes('*') || line.includes('+')),
      linesWithUnicodeBullets: lines.filter(line => bulletChars.some(ch => line.includes(ch))),
      step: 'BULLET_ANALYSIS'
    });
    
    const normalized = lines.map((line, index) => {
      const trimmed = line.trimStart();
      let modified = false;
      let result = line;
      
      for (const ch of bulletChars) {
        if (trimmed.startsWith(`${ch} `)) {
          const leadingSpaces = line.length - line.trimStart().length;
          result = `${' '.repeat(leadingSpaces)}- ${trimmed.slice(2)}`;
          modified = true;
          console.warn('🚨 [NORMALIZE_DEBUG] Unicode bullet converted:', {
            lineIndex: index,
            originalLine: line,
            normalizedLine: result,
            unicodeBullet: ch,
            step: 'UNICODE_BULLET_CONVERTED'
          });
          break;
        }
      }
      
      return result;
    }).join('\n');
    
    console.warn('🚨 [NORMALIZE_DEBUG] === NORMALIZATION COMPLETE ===', {
      originalMarkdown: markdown,
      normalizedMarkdown: normalized,
      normalizationChanged: normalized !== markdown,
      originalBulletLines: markdown.split(/\r?\n/).filter(line => line.trim().match(/^[-*+]\s/)),
      normalizedBulletLines: normalized.split(/\r?\n/).filter(line => line.trim().match(/^[-*+]\s/)),
      step: 'NORMALIZATION_COMPLETE'
    });
    
    return normalized;
  }

  describe(): string {
    const targetDesc = `range ${this.target.from}..${this.target.to}`;
    return `Replace ${targetDesc} with Markdown`;
  }

  /**
   * 🔧 HELPER: Check if text contains markdown syntax that needs re-parsing
   */
  private containsMarkdownSyntax(text: string): boolean {
    if (!text || typeof text !== 'string') {
      log.log('🔍 [MARKDOWN_SYNTAX_CHECK] Invalid text:', { text: typeof text, textValue: text });
      return false;
    }
    
    // Check for common markdown patterns that marked.lexer might miss in list items
    const patterns = [
      /\[.+?\]\(.+?\)/,           // Links: [text](url)
      /\[.+?\]\[.+?\]/,           // Reference links: [text][ref]
      /\*\*.+?\*\*/,              // Bold: **text**
      /__.+?__/,                  // Bold: __text__
      /\*.+?\*/,                  // Italic: *text* (but not list bullets)
      /_.+?_/,                    // Italic: _text_
      /`.+?`/,                    // Code: `text`
      /~~.+?~~/,                  // Strikethrough: ~~text~~
    ];
    
    const results = patterns.map((pattern, index) => {
      const match = pattern.test(text);
      return { pattern: pattern.toString(), match, index };
    });
    
    const hasMarkdown = results.some(r => r.match);
    
    // log.log('🔍 [MARKDOWN_SYNTAX_CHECK] Pattern matching results:', {
    //   text: text.substring(0, 100),
    //   textLength: text.length,
    //   hasMarkdown,
    //   patternResults: results.filter(r => r.match).map(r => ({ pattern: r.pattern, index: r.index })),
    //   allPatternResults: results
    // });
    
    return hasMarkdown;
  }

  /**
   * 🔧 HELPER: Parse inline markdown content using marked's inline lexer
   */
  private parseInlineMarkdown(text: string): any[] {
    log.log('🔍 [YDOC_MARKED_DEBUG] === parseInlineMarkdown called ===', {
      timestamp: new Date().toISOString(),
      inputText: text,
      textLength: text.length
    });
    
    try {
      // Use marked to parse inline content by creating a temporary paragraph
      const tempMarkdown = text; // Don't wrap in paragraph to avoid block parsing
      
      log.log('🔍 [YDOC_MARKED_DEBUG] About to call marked.lexer from yDocLLMAPI:', {
        tempMarkdown,
        markedType: typeof marked,
        hasLexer: typeof marked.lexer,
        markedKeys: Object.keys(marked)
      });
      
      // ✅ USE CANONICAL CONFIG: Ensures consistent parsing with doc_write.ts
      const tokens = parseMarkdownToTokens(tempMarkdown);
      
      log.log('✅ [YDOC_MARKED_DEBUG] marked.lexer SUCCESS in yDocLLMAPI:', {
        tokenCount: tokens?.length || 0,
        tokenTypes: tokens?.map((t: any) => t.type) || []
      });
      
      if (tokens && tokens.length > 0) {
        const firstToken = tokens[0];
        
        // If it's a paragraph token, extract its inline tokens
        if (firstToken.type === 'paragraph' && firstToken.tokens) {
          log.log('🔧 [INLINE_PARSE_SUCCESS] Successfully parsed inline content:', {
            originalText: text.substring(0, 50),
            tokenCount: firstToken.tokens.length,
            tokenTypes: firstToken.tokens.map((t: any) => t.type),
            hasLinks: firstToken.tokens.some((t: any) => t.type === 'link')
          });
          return firstToken.tokens;
        }
        
        // If it's already inline tokens, return them
        if (firstToken.type === 'text' || firstToken.type === 'link' || firstToken.type === 'strong') {
          return tokens;
        }
      }
      
      // Fallback: try parsing as a wrapped paragraph
      // 🔧 FIX: Ensure trailing newline for proper block-level parsing
      const textForLexer = text.endsWith('\n') ? text : text + '\n';
      const wrappedTokens = marked.lexer(textForLexer, {
        gfm: true,
        breaks: false,
        sanitize: false,      // Don't HTML-encode entities
        smartypants: false,   // Don't convert quotes to smart quotes
        xhtml: false          // Don't use XHTML encoding
      });
      if (wrappedTokens && wrappedTokens.length > 0 && wrappedTokens[0].tokens) {
        return wrappedTokens[0].tokens;
      }
      
    } catch (error) {
      console.warn('🔧 [INLINE_PARSE_FALLBACK] Failed to parse inline markdown, using original text:', {
        text: text.substring(0, 50),
        error: error instanceof Error ? error.message : String(error)
      });
    }
    
    // Ultimate fallback: return as text token
    return [{ type: 'text', text: text }];
  }

  /**
   * 🚨 ENHANCED TEXT PRESERVATION HELPER
   * Extract any possible text from an unknown token to prevent content loss
   * Now handles specific token types like links, formatting, etc.
   */
  private extractTextFromUnknownToken(token: any): string {
    // 🎯 SPECIFIC TOKEN TYPE HANDLING
    if (token && typeof token === 'object' && token.type) {
      switch (token.type) {
        case 'link':
          // For links, prefer the display text over the URL
          if (token.text) return token.text;
          if (token.href) return token.href;
          break;
        case 'strong':
        case 'em':
        case 'emphasis':
        case 'code':
        case 'codespan':
        case 'del':
        case 'strike':
          // For formatting tokens, extract the inner text
          if (token.text) return token.text;
          if (token.tokens && Array.isArray(token.tokens)) {
            const innerTexts = token.tokens.map((t: any) => this.extractTextFromUnknownToken(t)).filter(Boolean);
            if (innerTexts.length > 0) return innerTexts.join('');
          }
          break;
        case 'text':
          if (token.text) return token.text;
          break;
        case 'def':
          // Link definitions: prefer title over href
          if (token.title) return token.title;
          if (token.href) return token.href;
          break;
      }
    }
    
    // 🛡️ FALLBACK: Try multiple methods to extract text
    const candidates: string[] = [];
    
    // Method 1: Direct text property
    if (token.text && typeof token.text === 'string') {
      candidates.push(token.text);
    }
    
    // Method 2: Raw property (original markdown)
    if (token.raw && typeof token.raw === 'string') {
      candidates.push(token.raw);
    }
    
    // Method 3: Nested tokens
    if (token.tokens && Array.isArray(token.tokens)) {
      for (const nestedToken of token.tokens) {
        const nestedText = this.extractTextFromUnknownToken(nestedToken);
        if (nestedText) {
          candidates.push(nestedText);
        }
      }
    }
    
    // Method 4: Link-specific properties
    if (token.href && typeof token.href === 'string') {
      candidates.push(token.href);
    }
    if (token.title && typeof token.title === 'string') {
      candidates.push(token.title);
    }
    
    // Method 5: Any other string properties
    for (const [key, value] of Object.entries(token)) {
      if (typeof value === 'string' && value.trim() && 
          key !== 'type' && key !== 'raw' && key !== 'href' && 
          !candidates.includes(value)) {
        candidates.push(value);
      }
    }
    
    // Return the longest substantial text found, preferring non-URL text
    const substantialTexts = candidates.filter(text => text.trim().length > 0);
    
    // Prefer text that doesn't look like a URL
    const nonUrlTexts = substantialTexts.filter(text => !text.startsWith('http') && !text.startsWith('#'));
    if (nonUrlTexts.length > 0) {
      return nonUrlTexts.reduce((longest, current) => 
        current.length > longest.length ? current : longest, '');
    }
    
    // Otherwise return the longest text
    if (substantialTexts.length > 0) {
      return substantialTexts.reduce((longest, current) => 
        current.length > longest.length ? current : longest, '');
    }
    
    // 🚨 ULTIMATE FALLBACK: If absolutely no text can be extracted, create a visible placeholder
    const tokenType = token?.type || 'unknown';
    const fallbackText = `[Unknown ${tokenType}: ${JSON.stringify(token).substring(0, 30)}...]`;
    console.error('🚨 [ULTIMATE_TEXT_FALLBACK] No text extractable from token - using placeholder:', {
      tokenType,
      tokenKeys: Object.keys(token || {}),
      placeholderText: fallbackText,
      reason: 'prevent_complete_content_loss'
    });
    return fallbackText;
  }

  // Reusable cleanup function for consistent post-insertion processing
  private applyPostInsertCleanup(
    elements: Y.XmlElement[],
    context: CommandContext,
    oldTextAggregate: string[],
    suggestionMode: boolean,
    revisionId: string,
    batchDescription: string
  ): void {
    // Apply "new" markings for suggestion/visual diff
    elements.forEach((element: Y.XmlElement) => {
      PatchLogger.applyDualMarking(element, 'insert', revisionId, suggestionMode);
    });
    
    // Log patches for new elements
    const newTextAggregate: string[] = [];
    elements.forEach((element: Y.XmlElement) => {
      const nodeId = element.getAttribute('id') as string;
      PatchLogger.logPatch(context, {
        patchId: `patch_${nanoid(12)}`,
        nodeId,
        op: 'insert',
        revisionId,
        status: suggestionMode ? 'pending' : 'accepted',
        batchDescription,
        authorType: 'agent',
      });
      try {
        const txt = (yDocumentService as any).extractTextContent(element);
        if (txt && txt.length) newTextAggregate.push(txt);
      } catch {}
    });
    
    // Store revision meta for downstream diff fallback
    try {
      const metaMap = context.doc.getMap('revision_meta');
      metaMap.set(revisionId, {
        oldText: oldTextAggregate.join('\n'),
        newText: newTextAggregate.join('\n'),
        description: batchDescription,
        timestamp: Date.now()
      } as any);
    } catch {}
  }

  getInsertedNodes(context: CommandContext): ReadResult['nodes'] {
    if (!this.insertedElements || this.insertedElements.length === 0) return [];
    return this.insertedElements.map(element => Read['elementToNode'](element));
  }

  getInsertedNodeIds(): string[] {
    return [];
  }

  getRevisionId(): string {
    return this.revisionId;
  }
}
